import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Vg2Shell } from "./Vg2Shell";
import { RequestContinuityBanner } from "@/components/vg2/RequestContinuityBanner";
import { RuleRefreshNotice, ValuationMethodDetails } from "@/components/vg2/ValuationMethodDetails";
import { getStructuredRequest, structuredRequestEnabled } from "@/lib/vg3StructuredRequest";
import { getValuationById, patchValutaAddress, runValuation } from "@/lib/vg2Valuta";
import { fmtEur, localizePrecisionActions, localizeGeoState, splitMunicipalityCap } from "@/lib/vg2Format";
import { createAnonAxios } from "@/lib/vg2AnonAxios";
import { partitionScenarios } from "@/lib/vg2Scenarios";
import { MercatoIntornoBlock } from "@/components/vg2/MercatoIntornoBlock";
import SaveAnalysisButton from "@/components/vg2/SaveAnalysisButton";

const API_V2 = `${process.env.REACT_APP_BACKEND_URL}/api/v2`;
const api = createAnonAxios(API_V2);

const CONFIDENCE_LABEL_IT = {
  HIGH: "Base informativa solida",
  MEDIUM: "Base informativa buona, con elementi migliorabili",
  LOW: "Base informativa orientativa",
};

const CONFIDENCE_DOTS = { HIGH: 3, MEDIUM: 2, LOW: 1 };

const OBJECTIVE_COPY_IT = {
  vendere: {
    label: "Vendere",
    lede: "Il valore indica un intervallo di partenza per la trattativa. La tenuta effettiva dipende da preparazione, tempistica e stato dei documenti.",
    checkpoints: [
      "Documenti urbanistici e catastali verificati",
      "Classe energetica aggiornata",
      "Presentazione (foto, planimetria, testi) coerente con la fascia",
    ],
  },
  affittare: {
    label: "Affittare",
    lede: "Il valore va letto insieme al canone sostenibile, ai costi ricorrenti e allo stato di occupazione dell’immobile.",
    checkpoints: [
      "Canone verificato con la microzona",
      "Costi condominiali e imposte stimati",
      "Regolarità catastale e APE aggiornata",
    ],
  },
  generare_reddito: {
    label: "Generare reddito",
    lede: "Con questo valore possiamo valutare il rendimento potenziale. Servono canone, costi ricorrenti e occupazione stabile per una stima coerente.",
    checkpoints: [
      "Canone di riferimento nella microzona",
      "Costi e imposte previsti",
      "Stato di occupazione e vincoli contrattuali",
    ],
  },
  capire_quanto_vale: {
    label: "Capire quanto vale",
    lede: "Hai il quadro economico dell’immobile. Il passo successivo è capire quale direzione ha senso rispetto ai tuoi tempi.",
    checkpoints: [
      "Confronta le direzioni possibili in DECIDI",
      "Valuta stato conservativo e documenti",
      "Rifletti sull’orizzonte temporale",
    ],
  },
  capire_quale_scelta_conviene: {
    label: "Capire quale scelta conviene",
    lede: "Il valore ti permette di mettere a fuoco alternative concrete: vendere, affittare, mantenere o ristrutturare.",
    checkpoints: [
      "Confronto delle strade in DECIDI",
      "Verifica preparazione documentale",
      "Bilancio dei costi rispetto al valore",
    ],
  },
  altro: {
    label: "Altro obiettivo",
    lede: "Il valore ti dà un punto di partenza. Il modo di usarlo dipende dall’obiettivo concreto che vuoi mettere a fuoco.",
    checkpoints: ["Confronta le direzioni in DECIDI"],
  },
};

const TIMEFRAME_COPY_IT = {
  asap: "Il prima possibile",
  few_months: "Entro pochi mesi",
  within_year: "Entro un anno",
  no_deadline: "Nessuna scadenza precisa",
};

// §VALUTA-PROJECT · disclaimer ufficiale (verbatim) per le sezioni che
// interpretano il risultato rispetto al progetto dell'utente.
const OFFICIAL_DISCLAIMER_IT =
  "Le stime derivano dall’elaborazione digitale dei dati disponibili. " +
  "Un Property Advisor (PA) può aiutarti a interpretare i risultati in " +
  "relazione al tuo progetto immobiliare.";

const STRATEGY_LABEL_IT = {
  short_term_rent: "Casa vacanze / affitto turistico",
  student_rent: "Affitto a studenti",
  transitional_rent: "Affitto transitorio",
  long_term_rent: "Affitto a lungo termine",
  sell: "Vendita",
  renovate: "Ristrutturazione e valorizzazione",
  buy: "Acquisto",
};
const LINKED_LABEL_IT = {
  rent_larger_home: "affittare un’abitazione più grande nella stessa zona",
  buy_other: "acquistare un altro immobile",
};
const CONDITION_LABEL_IT = {
  liquidity_margin: "mantenere un margine di liquidità mensile",
};

// Costruisce la narrativa PERSONALIZZATA "Il valore rispetto al tuo
// obiettivo" dalla comprensione strutturata del progetto (deterministica).
function buildProjectMeta(pu) {
  if (!pu) return null;
  if (pu.source === "structured_request") {
    const ops = pu.operations || [];
    const list = ops.map((o) => o.toLowerCase());
    if (ops.length >= 2) {
      return {
        label: pu.goal_label || "Capire quale scelta conviene",
        lede: `Hai chiesto di capire quale scelta convenga tra: ${list.slice(0, -1).join(", ")} e ${list[list.length - 1]}. `
          + "Questo valore è il punto di partenza comune: in DECIDI ogni operazione richiesta viene calcolata e confrontata.",
        checkpoints: [...ops.map((o) => `Operazione richiesta: ${o}`), "Ipotesi dichiarate e fascia di incertezza dei dati"],
      };
    }
    if (ops.length === 1) {
      return {
        label: ops[0],
        lede: `Hai chiesto di valutare: ${list[0]}. Questo valore è il punto di partenza: in DECIDI l’operazione viene calcolata con costi, ricavi e tempi.`,
        checkpoints: [`Operazione richiesta: ${ops[0]}`, "Ipotesi dichiarate e fascia di incertezza dei dati"],
      };
    }
    return pu.goal_label ? { label: pu.goal_label, lede: "Questo è il valore di mercato stimato dell’immobile oggi.", checkpoints: [] } : null;
  }
  const linked = (pu.linked_objectives || [])
    .map((k) => LINKED_LABEL_IT[k]).filter(Boolean);
  const conds = (pu.conditions || [])
    .map((k) => CONDITION_LABEL_IT[k]).filter(Boolean);

  if (pu.main_explicit && pu.main_strategy) {
    const strat = STRATEGY_LABEL_IT[pu.main_strategy] || "la strategia indicata";
    const isRent = ["short_term_rent", "transitional_rent", "student_rent", "long_term_rent"]
      .includes(pu.main_strategy);

    if (pu.main_strategy === "sell") {
      const checkpoints = [
        "Prezzo di vendita realizzabile stimato",
        "Costi di vendita e margine netto",
        "Ipotesi dichiarate e fascia di incertezza dei dati",
      ];
      return {
        label: strat,
        lede: "Abbiamo letto il tuo progetto: vendere l’immobile. L’analisi "
          + "economica stima il ricavo realizzabile al netto dei costi di "
          + "vendita, con ipotesi esplicite e fasce di incertezza.",
        checkpoints,
      };
    }
    if (pu.main_strategy === "renovate") {
      const checkpoints = [
        "Valore stimato dopo i lavori",
        "Costo dell’intervento e margine atteso",
        "Ipotesi dichiarate e fascia di incertezza dei dati",
      ];
      return {
        label: strat,
        lede: "Abbiamo letto il tuo progetto: ristrutturare e valorizzare "
          + "l’immobile. L’analisi mette a confronto il valore attuale con "
          + "quello stimato dopo i lavori, al netto del costo dell’intervento, "
          + "con ipotesi esplicite e fasce di incertezza.",
        checkpoints,
      };
    }
    if (pu.main_strategy === "buy") {
      return {
        label: strat,
        lede: "Abbiamo letto il tuo progetto: acquistare l’immobile. "
          + "L’analisi valuta la congruità del prezzo rispetto ai valori "
          + "della zona, con ipotesi esplicite e fasce di incertezza.",
        checkpoints: [
          "Congruità del prezzo rispetto alla zona",
          "Ipotesi dichiarate e fascia di incertezza dei dati",
        ],
      };
    }

    // Strategie di locazione/reddito: ricavi, costi, reddito netto.
    let lede = `Abbiamo letto il tuo progetto: usare l’immobile come `
      + `${strat.toLowerCase()}.`;
    if (linked.length) lede += ` Obiettivo collegato: ${linked.join(" e ")}.`;
    if (conds.length) lede += ` Condizione da rispettare: ${conds.join(" e ")}.`;
    lede += ` L’analisi economica principale parte da questa strategia — `
      + `non dall’affitto ordinario — e mette in relazione ricavi, costi e `
      + `reddito netto, con ipotesi esplicite e fasce di incertezza.`;
    const checkpoints = [`Ricavi e reddito netto stimati per: ${strat}`];
    linked.forEach((l) => checkpoints.push(`Costo per ${l}`));
    if (conds.length) {
      checkpoints.push(`Margine residuo rispetto a: ${conds.join(" e ")}`);
    }
    checkpoints.push("Ipotesi dichiarate e fascia di incertezza dei dati");
    return { label: strat, lede, checkpoints, isRent };
  }

  if (pu.is_comparison && pu.reference_strategy) {
    const ref = STRATEGY_LABEL_IT[pu.reference_strategy] || "affitto a lungo termine";
    const alts = (pu.alternatives || [])
      .map((k) => STRATEGY_LABEL_IT[k]).filter(Boolean);
    const _RENT_KEYS = ["long_term_rent", "short_term_rent", "student_rent", "transitional_rent"];
    // Post-collaudo · "forme di locazione" solo se TUTTE le operazioni a confronto sono locazioni.
    const isRentComparison = [pu.reference_strategy, ...(pu.alternatives || [])].every((k) => _RENT_KEYS.includes(k));
    const subject = isRentComparison ? "forma di locazione" : "operazione";
    const lede = `Hai chiesto di capire quale ${subject} convenga. `
      + `Usiamo ${ref.toLowerCase()} come riferimento iniziale e lo `
      + `confrontiamo con le alternative pertinenti all’immobile e alla zona.`;
    const checkpoints = [`Riferimento iniziale: ${ref}`];
    if (alts.length) {
      checkpoints.push(
        `${isRentComparison ? "Forme" : "Operazioni"} a confronto: ${alts.join(", ")}`);
    }
    checkpoints.push("Ipotesi dichiarate e fascia di incertezza dei dati");
    const label = isRentComparison
      ? "Confronto tra forme di locazione"
      : "Confronto tra operazioni";
    return { label, lede, checkpoints };
  }

  if (pu.reference_strategy) {
    const ref = STRATEGY_LABEL_IT[pu.reference_strategy] || "affitto a lungo termine";
    const alts = (pu.alternatives || [])
      .map((k) => STRATEGY_LABEL_IT[k]).filter(Boolean);
    let lede = `Hai indicato un obiettivo ampio. Partiamo da `
      + `${ref.toLowerCase()} come riferimento iniziale e lo confrontiamo `
      + `con le strade alternative pertinenti all’immobile e alla zona.`;
    const checkpoints = [`Riferimento iniziale: ${ref}`];
    if (alts.length) checkpoints.push(`Alternative a confronto: ${alts.join(", ")}`);
    checkpoints.push("Ipotesi dichiarate e fascia di incertezza dei dati");
    return { label: "Mettere a reddito", lede, checkpoints };
  }
  return null;
}

export default function ValutaResult() {
  const { journeyId, analysisId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const emailFiredRef = React.useRef(false);
  const [emailStatus, setEmailStatus] = React.useState(null);

  React.useEffect(() => {
    (async () => {
      try {
        const ctx = (await api.get(`/journeys/${journeyId}/valuta/context`)).data;
        const detail = await getValuationById(ctx.case_id, analysisId);
        setData({ ctx, analysis: detail });
      } catch {
        setError("Non riesco a caricare il risultato.");
      }
    })();
  }, [journeyId, analysisId]);

  // §10 policy: no auto-send from VALUTA. The single final email is
  // dispatched only at the end of AGISCI via an explicit CTA.

  if (error) {
    return (
      <Vg2Shell>
        <div className="pt-24" data-testid="valuta-result-error">
          <p role="alert" className="text-vg2-error">{error}</p>
        </div>
      </Vg2Shell>
    );
  }
  if (!data) {
    return (
      <Vg2Shell>
        <div className="pt-24 text-vg2-muted" data-testid="valuta-result-loading">
          Sto preparando la valutazione…
        </div>
      </Vg2Shell>
    );
  }

  const analysis = data.analysis;
  const result = analysis.valuation_result || {};
  // §valuta-single · mostra l'obiettivo SOLO se dichiarato dall'utente.
  // Se è stato impostato internamente di default (capire_quanto_vale senza
  // scelta), non lo attribuiamo all'utente: objectiveCode resta null.
  const _intent = data.ctx?.capisci_intent || {};
  const objectiveCode = (_intent.objective_explicit === false)
    ? null : (_intent.objective_code || null);
  const geoBanner = localizeGeoState(analysis.geo_state);
  // Never-terminal (§1) — after backend Level 6 prudent fallback we
  // always have a numeric range. DECIDI CTA is only gated when the
  // civico is genuinely required (street straddles multiple zones).
  const canProceedToDecidi = !(geoBanner && geoBanner.code === "needs_housenumber");

  return (
    <Vg2Shell>
      <div className="pt-12 pb-24 max-w-[820px]"
           data-testid={`valuta-result-${result.result_kind === "prudent_national" ? "prudent" : "numeric"}`}>
        <RequestContinuityBanner journeyId={journeyId} analysisId={analysisId} />
        {geoBanner && (
          <GeoStateBanner key={analysisId} banner={geoBanner}
                          journeyId={journeyId}
                          caseId={data.ctx?.case_id}
                          address={data.ctx?.property?.address || {}} />
        )}
        <NumericView result={result} data={data}
                     analysis={analysis}
                     analysisId={analysisId}
                     companion={analysis.companion_valuation_result || null}
                     companionMode={analysis.companion_mode || null}
                     objectiveCode={objectiveCode}
                     canProceedToDecidi={canProceedToDecidi}
                     onDecidi={() => navigate(`/vg2/decidi/${data.ctx.case_id}`)} />
        <CharacteristicsCompleteness features={data.ctx?.property?.features || {}} />
        <PaIntroUnlessStructured journeyId={journeyId} />
        <div className="mt-8 pt-6 border-t border-vg2-line flex items-center gap-3 flex-wrap">
          <SaveAnalysisButton
            caseId={data.ctx?.case_id}
            analysisId={analysisId}
            testid="valuta-result-save"
          />
          <span className="text-xs text-vg2-muted">
            Salva questa valutazione nella tua dashboard per riaprirla quando vuoi.
          </span>
        </div>
        {process.env.NODE_ENV !== "production" && emailStatus && (
          <p data-testid="valuta-email-status" className="sr-only">
            email:{emailStatus}
          </p>
        )}
      </div>
    </Vg2Shell>
  );
}
// §I-3B-R · Completezza delle caratteristiche DICHIARATE, in testo semplice
// (nessun nuovo punteggio numerico). Distinta dall'affidabilità dei dati
// territoriali e dalla precisione della stima. Quando mancano caratteristiche
// dichiara l'ipotesi usata (stima sui soli dati essenziali + zona).
const _COMPLETENESS = [
  ["rooms", "locali"], ["bathrooms", "bagni"], ["floor", "piano"], ["elevator", "ascensore"], ["condition", "stato"],
  ["exposure", "esposizione"], ["brightness", "luminosità"], ["view", "vista"], ["occupancy", "occupazione"],
  ["pertinenze", "pertinenze"], ["free_text_notes", "caratteristiche distintive"],
];
const _PERT_KEYS = ["balcony", "terrace", "garden", "cellar", "attic", "box", "parking"];
function _isDeclared(f, k) {
  if (k === "pertinenze") return !!f.pertinenze_declared || _PERT_KEYS.some((p) => f[p] === true);
  const v = f[k];
  if (v === undefined || v === null) return false;
  if (typeof v === "string") return v.trim() !== "" && v.trim().toLowerCase() !== "unknown";
  return true;
}
function CharacteristicsCompleteness({ features }) {
  const f = features || {};
  const given = _COMPLETENESS.filter(([k]) => _isDeclared(f, k)).map(([, l]) => l);
  const missing = _COMPLETENESS.filter(([k]) => !_isDeclared(f, k)).map(([, l]) => l);
  return (
    <section data-testid="valuta-characteristics-completeness"
             className="mt-8 border border-vg2-line rounded-lg p-6 bg-white">
      <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-2">
        Caratteristiche usate nella stima
      </p>
      <p className="text-vg2-ink leading-relaxed" data-testid="valuta-characteristics-given">
        {given.length ? <>Hai indicato: <span className="font-medium">{given.join(", ")}</span>.</> : "Non hai indicato caratteristiche facoltative."}
      </p>
      {missing.length > 0 && (
        <p className="text-vg2-muted text-sm leading-relaxed mt-2" data-testid="valuta-characteristics-missing">
          Non indicate: {missing.join(", ")}. Per queste la stima non applica né premi né penalità; aggiungerle può renderla più mirata.
        </p>
      )}
    </section>
  );
}





// §valuta-single · Blocco Property Advisor (portato dalla ex-pagina esito
// CAPISCI). Resta raggiungibile DOPO il risultato; la condizione di accesso
// è la stessa: CTA WhatsApp solo se il contatto advisor è configurato.
// Post-collaudo · con la Richiesta strutturata il Property Advisor si propone in AGISCI (fase operativa).
function PaIntroUnlessStructured({ journeyId }) {
  const [structured, setStructured] = React.useState(null);
  React.useEffect(() => {
    (async () => {
      try {
        const on = await structuredRequestEnabled();
        setStructured(on ? !!(await getStructuredRequest(journeyId))?.persisted : false);
      } catch { setStructured(false); }
    })();
  }, [journeyId]);
  return structured === false ? <PropertyAdvisorIntroBlock /> : null;
}

function PropertyAdvisorIntroBlock() {
  const [waHref, setWaHref] = React.useState(null);
  const [available, setAvailable] = React.useState(null);
  React.useEffect(() => {
    (async () => {
      try {
        const axiosMod = await import("axios");
        const client = axiosMod.default.create({
          baseURL: `${process.env.REACT_APP_BACKEND_URL}/api/v2`,
        });
        const r = await client.get("/public/config");
        const wa = r?.data?.advisor_whatsapp;
        if (wa?.configured && wa.e164) {
          const msg = encodeURIComponent(
            "Ciao, sto usando ValutaGenio e vorrei un orientamento gratuito con un Property Advisor."
          );
          setWaHref(`https://wa.me/${wa.digits_only}?text=${msg}`);
          setAvailable(true);
        } else {
          setAvailable(false);
        }
      } catch (e) { setAvailable(false); }
    })();
  }, []);
  return (
    <section
      data-testid="capisci-pa-intro"
      className="mt-16 mb-6 border border-vg2-line rounded-lg p-8 bg-white"
    >
      <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-4">
        Dal valore stimato al valore reale
      </p>
      <p className="text-vg2-ink leading-relaxed mb-8 max-w-[700px]">
        ValutaGenio analizza il tuo immobile, ne stima il valore e individua le
        opportunità. Ma ogni immobile ha caratteristiche che i dati, da soli,
        non possono raccontare completamente: lo stato effettivo, la qualità
        degli spazi, le condizioni del mercato locale e molti altri elementi che
        possono incidere sul prezzo. Per questo è importante il confronto con un
        Property Advisor: un professionista che può verificare questi fattori,
        affinare la valutazione e aiutarti a capire quali opportunità siano
        concretamente realizzabili.
      </p>
      <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-3">
        Perché questo orientamento è gratuito?
      </p>
      <p className="text-vg2-muted text-sm leading-relaxed mb-6 max-w-[700px]">
        Il confronto parte soltanto da una tua richiesta. Non comporta
        incarichi o impegni commerciali. Eventuali servizi successivi saranno
        valutati e accettati da te, in modo esplicito.
      </p>
      {waHref && (
        <a
          href={waHref}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="capisci-pa-cta"
          className="inline-flex items-center gap-3 rounded-full border border-vg2-red text-vg2-red hover:bg-vg2-red hover:text-white px-6 py-3 text-sm font-semibold transition-colors"
        >
          Parla gratuitamente con un Property Advisor
        </a>
      )}
      {available === false && (
        <p data-testid="capisci-pa-cta-fallback"
           className="text-sm text-vg2-muted">
          Il contatto diretto sarà attivato tra pochi giorni. Nel frattempo,
          puoi completare il percorso e ricevere l’analisi via email.
        </p>
      )}
    </section>
  );
}


// ------------------------------------------------------------ GeoState banner

function GeoStateBanner({ banner, journeyId, caseId, address }) {
  const navigate = useNavigate();
  const isRetry = banner.code === "unavailable";
  const [open, setOpen] = React.useState(false);
  const [street, setStreet] = React.useState(address?.street || "");
  const [municipality, setMunicipality] = React.useState(address?.municipality || "");
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);

  // Max attesa lato client: la stima non resta mai in caricamento per
  // sempre. Se scade, mostriamo un messaggio utile (i dati restano).
  const MAX_WAIT_MS = 150000;
  const withTimeout = (p) => Promise.race([
    p,
    new Promise((_, rej) => setTimeout(
      () => rej(new Error("__timeout__")), MAX_WAIT_MS)),
  ]);

  const revaluate = async () => {
    // Fresh op-id so the backend re-geocodes and produces a NEW analysis
    // (idempotency keys off operation_id) — same case, no duplicate.
    const op = "vop_fix_" + Math.random().toString(36).slice(2, 12);
    const resp = await withTimeout(runValuation(caseId, op));
    navigate(`/vg2/valuta/${journeyId}/risultato/${resp.analysis_id}`);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    // CAP scritto nel campo Comune ("00128, Roma" / "Roma 00128"): lo
    // separiamo e teniamo solo il nome del comune (valore interno pulito).
    const { comune } = splitMunicipalityCap(municipality);
    if (street.trim().length < 3 || comune.length < 2) {
      setErr("Inserisci via (e civico) e comune — es. «Viale dei Caduti per la Resistenza, 65» e «Roma».");
      return;
    }
    setBusy(true);
    try {
      await withTimeout(patchValutaAddress(journeyId, street.trim(), comune));
      await revaluate();
    } catch (e2) {
      setErr(
        e2?.message === "__timeout__"
          ? "La stima sta impiegando più del previsto. I dati inseriti sono salvati: riprova tra poco."
          : (e2?.response?.data?.detail?.message
             || "Non è stato possibile aggiornare l’indirizzo. Riprova."),
      );
      setBusy(false);
    }
  };

  const retry = async () => {
    setErr(null);
    setBusy(true);
    try {
      await revaluate();
    } catch (e2) {
      setErr(e2?.message === "__timeout__"
        ? "La stima sta impiegando più del previsto. Riprova tra poco."
        : "Riprova tra poco: non siamo riusciti a ricollegare l’indirizzo.");
      setBusy(false);
    }
  };

  return (
    <section data-testid={`valuta-geo-state-${banner.code}`}
             className="mb-8 border-l-4 border-vg2-red pl-5 py-3 bg-vg2-red/[0.03] rounded-r">
      <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-2">
        {banner.title}
      </p>
      <p className="text-vg2-ink text-sm leading-relaxed max-w-[620px] mb-3"
         data-testid="valuta-geo-state-body">
        {banner.body}
      </p>

      {isRetry ? (
        <button type="button" onClick={retry} disabled={busy}
                data-testid="valuta-geo-state-cta"
                className="inline-flex items-center gap-2 px-5 py-2 border border-vg2-red text-vg2-red rounded-full hover:bg-vg2-red hover:text-white transition-colors text-sm font-medium disabled:opacity-40">
          {busy ? "Riprovo…" : banner.cta}
        </button>
      ) : !open ? (
        <button type="button" onClick={() => setOpen(true)}
                data-testid="valuta-geo-state-cta"
                className="inline-flex items-center gap-2 px-5 py-2 border border-vg2-red text-vg2-red rounded-full hover:bg-vg2-red hover:text-white transition-colors text-sm font-medium">
          {banner.cta}
        </button>
      ) : (
        <form onSubmit={submit} className="mt-2 space-y-3 max-w-[460px]"
              data-testid="valuta-geo-state-fix-form">
          <div>
            <label className="block text-xs text-vg2-muted mb-1">Indirizzo (via e civico)</label>
            <input data-testid="valuta-geo-fix-street"
                   value={street} onChange={(e) => setStreet(e.target.value)}
                   placeholder="Es. Via Vincenzo Monti 4"
                   className="w-full border border-vg2-line rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-vg2-red" />
          </div>
          <div>
            <label className="block text-xs text-vg2-muted mb-1">Comune</label>
            <input data-testid="valuta-geo-fix-municipality"
                   value={municipality} onChange={(e) => setMunicipality(e.target.value)}
                   placeholder="Es. Roma"
                   className="w-full border border-vg2-line rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-vg2-red" />
            <p className="text-[11px] text-vg2-muted mt-1">
              Scrivi solo il nome del comune (il CAP non serve: se lo scrivi qui, lo separiamo noi).
            </p>
          </div>
          {err && (
            <p role="alert" data-testid="valuta-geo-fix-error"
               className="text-vg2-error text-xs">{err}</p>
          )}
          <div className="flex items-center gap-3">
            <button type="submit" disabled={busy}
                    data-testid="valuta-geo-fix-submit"
                    className="px-5 py-2 bg-vg2-red text-white rounded-full text-sm font-medium hover:bg-vg2-red-hover disabled:opacity-40 transition-colors">
              {busy ? "Aggiorno la stima…" : "Aggiorna l’indirizzo"}
            </button>
            <button type="button" onClick={() => setOpen(false)} disabled={busy}
                    data-testid="valuta-geo-fix-cancel"
                    className="text-vg2-muted text-sm hover:text-vg2-ink">
              Annulla
            </button>
          </div>
        </form>
      )}
    </section>
  );
}


// ------------------------------------------------------------- Numeric view

function _isRentUnit(u) {
  return (u || "").toLowerCase().includes("month");
}

// --- Hero-from-decision selector ------------------------------------------
//
// The main number on VALUTA follows the top scenario chosen by EVA³
// (highest pertinence) — NOT a formula per objective. Every strategy
// declares whether its natural KPI is sale-side or rent-side; we pick
// the corresponding number from `economic_analysis` when the motor
// provides one, and always fall back to the raw commercial value.
//
// New scenarios plug in with one entry — no if/else per obiettivo.
const _STRATEGY_KIND = {
  // Sale-side strategies — hero stays as commercial value (degenerate).
  SELL_NOW: "sale",
  PREPARE_THEN_SELL: "sale",
  RENOVATE_THEN_SELL: "sale",
  SPLIT_AND_SELL: "sale",
  RENOVATE_AND_SPLIT_THEN_SELL: "sale",
  PREPARE_AND_SPLIT_THEN_SELL: "sale",
  HOLD: "sale",     // no economic numbers → commercial value
  // Rent-side strategies — hero becomes monthly amount from the motor.
  LONG_TERM_RENT: "rent",
  SHORT_TERM_RENT: "rent",
  STUDENT_RENT: "rent",
  RENOVATE_THEN_LONG_TERM_RENT: "rent",
  RENOVATE_THEN_STUDENT_RENT: "rent",
  RENOVATE_THEN_SHORT_TERM_RENT: "rent",
  SPLIT_THEN_LONG_TERM_RENT: "rent",
  // Open idea has no fixed KPI → fall back to commercial value.
  OPEN_IDEA: "sale",
};

// §BUGFIX-OBJ · Metadati per operazione di reddito: attività dichiarata e
// sostantivi coerenti per l'indicatore principale. Guida l'etichetta della
// rendita LORDA mostrata in VALUTA, senza ricondurre al canone ordinario.
const _RENT_OP_META = {
  SHORT_TERM_RENT: { activity: "casa vacanze", monthlyNoun: "Rendita lorda media mensile", annualNoun: "Rendita lorda annua" },
  LONG_TERM_RENT: { activity: "locazione ordinaria", monthlyNoun: "Canone lordo mensile", annualNoun: "Canone lordo annuo" },
  STUDENT_RENT: { activity: "affitto a studenti", monthlyNoun: "Rendita lorda media mensile", annualNoun: "Rendita lorda annua" },
  RENOVATE_THEN_LONG_TERM_RENT: { activity: "locazione ordinaria dopo i lavori", monthlyNoun: "Canone lordo mensile", annualNoun: "Canone lordo annuo" },
  RENOVATE_THEN_STUDENT_RENT: { activity: "affitto a studenti dopo i lavori", monthlyNoun: "Rendita lorda media mensile", annualNoun: "Rendita lorda annua" },
  RENOVATE_THEN_SHORT_TERM_RENT: { activity: "casa vacanze dopo i lavori", monthlyNoun: "Rendita lorda media mensile", annualNoun: "Rendita lorda annua" },
  SPLIT_THEN_LONG_TERM_RENT: { activity: "locazione ordinaria dei frazionati", monthlyNoun: "Canone lordo mensile", annualNoun: "Canone lordo annuo" },
};

function _midRange(range) {
  if (!range) return null;
  const lo = Number(range.low);
  const hi = Number(range.high);
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return null;
  return (lo + hi) / 2;
}

// Returns { mode, amount, suffix, primaryLabel, sourceScenarioTitle,
// sourceScenarioKey } or `null` if the scenarios aren't loaded yet.
function pickHeroFromDecision(scenarios, saleValue) {
  const saleFallback = () => ({
    mode: "sale",
    amount: saleValue,
    suffix: "",
    primaryLabel: "Valore attuale stimato",
    sourceScenarioTitle: null,
    sourceScenarioKey: null,
  });
  if (!Array.isArray(scenarios) || scenarios.length === 0) return saleFallback();
  // Backend returns scenarios already sorted by pertinence desc.
  // §CARD-EXCLUSION · l'hero si costruisce sullo scenario "puro"
  // (exclude_from_cards) quando presente: è quello che genera il numero
  // in cima. In modalità vendita è ``sell_as_is`` (kind sale → si cade
  // sul valore commerciale); in affitto/casa vacanze/studenti è lo
  // scenario di rendita puro che alimenta il numero /mese.
  const top = scenarios.find((s) => s.exclude_from_cards)
    || scenarios.find((s) => s.is_primary)
    || scenarios[0];
  // Backend emits scenario_key in UPPER_SNAKE (SELL_NOW, LONG_TERM_RENT…).
  // Normalise defensively so a contract drift on either side doesn't
  // silently drop the whole selector back to sale-fallback.
  const rawKey = String(top?.scenario_key || "").toUpperCase();
  const kind = _STRATEGY_KIND[rawKey] || null;
  const econ = top.economic_analysis || null;

  if (kind === "rent") {
    // §BUGFIX-OBJ · Per un'operazione di REDDITO l'indicatore principale è la
    // rendita LORDA media mensile dell'attività dichiarata (casa vacanze,
    // studenti, locazione ordinaria…), con la rendita lorda ANNUA come dato
    // secondario (= media mensile × 12). Mai ricondurre al canone ordinario
    // quando l'operazione dichiarata è un'altra.
    const op = _RENT_OP_META[rawKey]
      || { activity: "messa a reddito", monthlyNoun: "Rendita lorda media mensile", annualNoun: "Rendita lorda annua" };
    // Lordo annuo di scenario: centrale esplicito se disponibile (mediana
    // osservata), altrimenti punto medio della banda.
    const scenGrossAnnual = econ
      ? (Number.isFinite(econ.annual_gross_rent_central)
          ? econ.annual_gross_rent_central
          : _midRange(econ.annual_gross_rent_range))
      : null;

    // §CORREZIONE5 · Casa vacanze/B&B: gerarchia fonti/fallback per la
    // rendita lorda ESPLORATIVA (solo VALUTA, non tocca scenari/punteggi).
    //  B) dato web sufficientemente specifico → usa la stima di mercato.
    //  C) dato web poco specifico → media 50/50 tra web e (canone ord. ×2×1,15).
    //  D) nessun dato web → canone ordinario × 2,30 (convenzionale).
    // Annua = mensile × 12. Il convenzionale è marcato internamente.
    if (rawKey === "SHORT_TERM_RENT" || rawKey === "RENOVATE_THEN_SHORT_TERM_RENT") {
      const prov = (econ && econ.gross_revenue_provenance) || {};
      const spec = String(prov.specificity_level || "").toLowerCase();
      const webSource = ["ai_research", "market_research", "benchmark_admin"]
        .includes(prov.source);
      const webMonthly = (Number.isFinite(scenGrossAnnual) && scenGrossAnnual > 0)
        ? scenGrossAnnual / 12 : null;
      const ordinaryMonthly = _ordinaryMonthlyFromScenarios(scenarios);
      const specific = webSource && (spec === "locale" || spec === "benchmark");
      const webNonSpecific = webSource
        && (spec === "comune" || spec === "area_vicina" || spec === "proxy");

      let monthly = null, conventional = false, rule = null;
      if (webMonthly && specific) {
        monthly = webMonthly; rule = "B_web_specifico";
      } else if (webMonthly && webNonSpecific && Number.isFinite(ordinaryMonthly)) {
        monthly = 0.5 * webMonthly + 0.5 * (ordinaryMonthly * 2 * 1.15);
        rule = "C_media_web_convenzionale"; conventional = true;
      } else if (webMonthly && webNonSpecific) {
        monthly = webMonthly; rule = "B_web_poco_specifico_senza_ordinario";
      } else if (Number.isFinite(ordinaryMonthly) && ordinaryMonthly > 0) {
        monthly = ordinaryMonthly * 2.30; rule = "D_convenzionale_230"; conventional = true;
      } else if (webMonthly) {
        monthly = webMonthly; rule = "B_web_senza_ordinario";
      }
      if (Number.isFinite(monthly) && monthly > 0) {
        return {
          mode: "rent",
          amount: monthly,
          suffix: " /mese",
          primaryLabel: `${op.monthlyNoun} · ${op.activity}`,
          grossAnnual: monthly * 12,
          activity: op.activity,
          annualNoun: op.annualNoun,
          conventional,
          rule,
          sourceScenarioTitle: top.title,
          sourceScenarioKey: top.scenario_key,
        };
      }
      return {
        mode: "rent-insufficient",
        activity: op.activity,
        sourceScenarioTitle: top?.title,
        sourceScenarioKey: top?.scenario_key,
      };
    }

    // Altre operazioni di reddito (locazione ordinaria, studenti…): uso
    // diretto del lordo di scenario.
    if (Number.isFinite(scenGrossAnnual) && scenGrossAnnual > 0) {
      return {
        mode: "rent",
        amount: scenGrossAnnual / 12,
        suffix: " /mese",
        primaryLabel: `${op.monthlyNoun} · ${op.activity}`,
        grossAnnual: scenGrossAnnual,
        activity: op.activity,
        annualNoun: op.annualNoun,
        sourceScenarioTitle: top.title,
        sourceScenarioKey: top.scenario_key,
      };
    }
    return {
      mode: "rent-insufficient",
      activity: op.activity,
      sourceScenarioTitle: top?.title,
      sourceScenarioKey: top?.scenario_key,
    };
  }
  // Sale scenarios fall through to the commercial (patrimonial) value.
  return saleFallback();
}

// §BUGFIX-OBJ-FALLBACK · canone ORDINARIO mensile stimato, dallo scenario
// long_term_rent (lordo annuo / 12). Base per la stima convenzionale della
// casa vacanze quando mancano dati di mercato osservati.
function _ordinaryMonthlyFromScenarios(scenarios) {
  const lt = (scenarios || []).find(
    (s) => String(s?.scenario_key || "").toUpperCase() === "LONG_TERM_RENT");
  const econ = lt?.economic_analysis || null;
  if (!econ) return null;
  const grossAnnual = Number.isFinite(econ.annual_gross_rent_central)
    ? econ.annual_gross_rent_central
    : _midRange(econ.annual_gross_rent_range);
  return (Number.isFinite(grossAnnual) && grossAnnual > 0) ? grossAnnual / 12 : null;
}

// §CEO-PRES-UI · valore commerciale mostrato = corretto per lo stato
// (coerente con CapitalValueBlock); nessun ricalcolo, solo display.
function _saleDisplayCentral(saleRes) {
  const ca = saleRes?.condition_adjustment;
  if (ca && ca.applied && ca.current_absolute_value_central != null) {
    return ca.current_absolute_value_central;
  }
  return saleRes?.absolute_value_central ?? null;
}

// §CEO-PRES-UI (GO parziale, punto 2) · Sintesi economica: tre letture
// principali della LOCAZIONE ORDINARIA + valore patrimoniale, da dati già
// presenti (nessun ricalcolo, nessuna formula nuova). Il potenziale turistico
// resta negli scenari alternativi, con cifre separate.
function EconomicSummaryBlock({ saleResult, rentResult, annualYieldPct }) {
  const commercial = _saleDisplayCentral(saleResult);
  const ordinaryMonthly = rentResult?.absolute_value_central ?? null;
  if (commercial == null && ordinaryMonthly == null && annualYieldPct == null) {
    return null;
  }
  return (
    <section className="mt-8 mb-8 rounded-xl border border-vg2-line p-5 bg-white"
             data-testid="valuta-economic-summary">
      <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-4">
        Sintesi economica · letture principali
      </p>
      <div className="grid gap-5 sm:grid-cols-3">
        {commercial != null && (
          <div data-testid="valuta-summary-commercial">
            <p className="text-xs text-vg2-muted mb-1">Valore commerciale stimato</p>
            <p className="font-display font-semibold text-2xl text-vg2-red leading-tight">
              {fmtEur(commercial)}
            </p>
            <p className="text-xs text-vg2-muted mt-1">prezzo indicativo dell’intero immobile</p>
          </div>
        )}
        {ordinaryMonthly != null && (
          <div data-testid="valuta-summary-ordinary-rent">
            <p className="text-xs text-vg2-muted mb-1">Canone mensile lordo · locazione ordinaria</p>
            <p className="font-display font-semibold text-2xl text-vg2-ink leading-tight">
              {fmtEur(ordinaryMonthly)}<span className="text-sm text-vg2-muted font-normal"> /mese</span>
            </p>
            <p className="text-xs text-vg2-muted mt-1">importo lordo indicativo</p>
          </div>
        )}
        {annualYieldPct != null && (
          <div data-testid="valuta-summary-yield">
            <p className="text-xs text-vg2-muted mb-1">Rendimento annuo lordo</p>
            <p className="font-display font-semibold text-2xl text-vg2-ink leading-tight">
              {annualYieldPct.toFixed(1).replace(".", ",")}%
            </p>
            <p className="text-xs text-vg2-muted mt-1">canone annuo ÷ valore, prima di costi, imposte e sfitti</p>
          </div>
        )}
      </div>
      <p className="text-xs text-vg2-muted mt-4 leading-relaxed max-w-[640px]">
        Le altre strade — incluso l’eventuale potenziale turistico (casa
        vacanze) — sono mostrate come scenari alternativi, con cifre separate,
        più sotto.
      </p>
    </section>
  );
}

// §CEO-PRES-UI (GO parziale, punto 1) · Accesso esplicito a DECIDI. Riusa la
// navigazione esistente (`onDecidi` → /vg2/decidi/:caseId). DECIDI non
// richiede una scelta registrata (genera la decisione se assente); non
// forziamo né selezioniamo alcuno scenario.
function GoToDecidiSection({ onDecidi, structured = false }) {
  return (
    <section className="border-t border-vg2-line pt-10 mt-8"
             data-testid="valuta-goto-decidi-section">
      <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-2">
        Prossimo passo
      </p>
      <h2 className="font-display font-semibold text-2xl text-vg2-ink mb-2"
          data-testid="valuta-goto-decidi-title">
        {structured ? "Ora confronta le strade che hai chiesto." : "Hai individuato una strada. Ora scopri come percorrerla."}
      </h2>
      <p className="text-vg2-ink leading-relaxed max-w-[640px] mb-5">
        {structured
          ? "In DECIDI trovi costi, ricavi, tempi e rischi di ogni operazione, affiancati, e puoi analizzarne una nel dettaglio."
          : "Con DECIDI puoi capire quali documenti verificare, quali passaggi affrontare e quali professionisti possono aiutarti a procedere."}
      </p>
      <button type="button" onClick={onDecidi}
              data-testid="valuta-goto-decidi-btn"
              className="inline-flex items-center gap-2 px-6 py-3 bg-vg2-red text-white rounded-full hover:bg-vg2-red-hover transition-colors font-semibold">
        Vai a DECIDI →
      </button>
      {!structured && (
        <p className="text-xs text-vg2-muted mt-3 max-w-[640px]">
          Suggerimento: se scegli una delle strade qui sopra, la checklist di
          DECIDI si concentrerà su quella direzione.
        </p>
      )}
    </section>
  );
}

function ContextualCapitalLine({ saleValue, sourceScenarioTitle }) {
  if (!Number.isFinite(saleValue) || !sourceScenarioTitle) return null;
  return (
    <p className="mt-3 text-sm text-vg2-muted max-w-[620px] leading-relaxed"
       data-testid="valuta-hero-context-line">
      Considerato il valore commerciale dell’immobile{" "}
      <span className="font-medium text-vg2-ink"
            data-testid="valuta-hero-context-capital">
        {fmtEur(saleValue)}
      </span>
      {" "}e il potenziale legato allo scenario{" "}
      <span className="italic">{sourceScenarioTitle}</span>, abbiamo
      valutato queste alternative che puoi confrontare più sotto.
    </p>
  );
}

function DecisionHeroLoading() {
  return (
    <section className="mt-8 mb-8" data-testid="valuta-hero-loading">
      <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-3">
        Un attimo…
      </p>
      <p className="font-display font-semibold leading-tight text-vg2-muted text-2xl md:text-3xl">
        Sto individuando la strada più coerente con il tuo obiettivo…
      </p>
    </section>
  );
}

function RentHeroBlock({ hero, caseId, analysisId }) {
  const deepDiveHref = (caseId && analysisId && hero?.sourceScenarioKey)
    ? `/vg2/valuta/scenario/${caseId}/${analysisId}/${hero.sourceScenarioKey}`
    : null;
  // §BUGFIX-OBJ · coerenza garantita: annuo = mensile (arrotondato) × 12.
  const monthly = Math.round(Number(hero.amount) || 0);
  const annual = monthly * 12;
  return (
    <section className="mt-8 mb-8" data-testid="valuta-hero-rent-block">
      <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-3"
         data-testid="valuta-hero-label">
        {hero.primaryLabel}
      </p>
      <p className="font-display font-semibold leading-none text-vg2-red"
         data-testid="valuta-hero-central">
        <span className="text-5xl md:text-6xl">{fmtEur(monthly)}</span>
        <span className="text-xl text-vg2-muted font-normal">{hero.suffix}</span>
      </p>
      <p className="mt-3 text-sm text-vg2-muted" data-testid="valuta-hero-annual-secondary">
        {hero.annualNoun || "Rendita lorda annua"}:{" "}
        <span className="font-semibold text-vg2-ink">{fmtEur(annual)}</span>
        <span className="text-vg2-muted"> · pari alla media mensile × 12</span>
      </p>
      {hero.conventional && (
        <p className="mt-2 text-xs text-vg2-muted max-w-[620px]"
           data-testid="valuta-hero-conventional-note">
          {hero.rule === "C_media_web_convenzionale"
            ? "Stima orientativa: media tra i dati web di zona (poco specifici) e un riferimento convenzionale (canone ordinario × 2 × 1,15)."
            : "Stima convenzionale orientativa (canone ordinario × 2,30) in assenza di dati di mercato osservati per la locazione turistica in questa zona."}
        </p>
      )}
      {deepDiveHref && (
        <a
          href={deepDiveHref}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="valuta-hero-deepdive-link"
          className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-vg2-red hover:underline"
        >
          Approfondisci con VALUTA <span aria-hidden>↗</span>
        </a>
      )}
    </section>
  );
}

function RentInsufficientBlock({ activity }) {
  return (
    <section className="mt-8 mb-8" data-testid="valuta-hero-rent-insufficient">
      <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-3">
        {activity ? `Rendita · ${activity}` : "Rendita stimata"}
      </p>
      <p className="font-display font-semibold leading-tight text-vg2-ink text-2xl md:text-3xl max-w-[620px]">
        Non abbiamo ancora dati sufficienti per stimare la rendita lorda
        {activity ? ` di ${activity}` : ""} in questa zona.
      </p>
      <p className="mt-3 text-sm text-vg2-muted max-w-[620px]">
        Non sostituiamo la stima con il canone di locazione ordinaria. Il
        valore patrimoniale dell’immobile resta indicato qui sotto.
      </p>
    </section>
  );
}

function NumericView({ result, data, analysis, analysisId, companion, companionMode, objectiveCode, canProceedToDecidi, onDecidi }) {
  const decisionEarly = useDecisionScenarios(data?.ctx?.case_id);
  const rawObjectiveMeta = OBJECTIVE_COPY_IT[objectiveCode] || null;
  // §M8.9 · Per "altro" con testo libero rewritato dall'LLM in
  // capisci_intent.objective_freetext_label, usiamo quello come label
  // così l'utente vede "Trasformare in B&B" invece di "Altro obiettivo".
  const freetextLabel = data?.ctx?.capisci_intent?.objective_freetext_label;
  const objectiveMeta = (rawObjectiveMeta && objectiveCode === "altro" && freetextLabel)
    ? { ...rawObjectiveMeta, label: freetextLabel }
    : rawObjectiveMeta;
  // §VALUTA-PROJECT · se il progetto scritto è stato compreso in modo
  // strutturato, la narrativa personalizzata ha priorità sulla copy statica.
  const projectUnderstanding = data?.ctx?.capisci_intent?.project_understanding || null;
  const _srRef = decisionEarly?.view?.structured_request_ref;
  // Post-collaudo · con la Richiesta strutturata l'obiettivo mostrato è quello confermato dall'utente.
  const projectMeta = _srRef ? {
    label: "Confronto tra le operazioni che hai chiesto",
    lede: `Hai chiesto di confrontare: ${(_srRef.requested_ops || []).map((o) => o.label).join(", ")}. `
      + "Qui vedi quanto vale l'immobile oggi; il confronto economico delle operazioni è in DECIDI.",
    checkpoints: (_srRef.requested_ops || []).map((o) => o.label),
  } : buildProjectMeta(projectUnderstanding);
  const effectiveObjectiveMeta = projectMeta || objectiveMeta;
  const hasMunicipality = !!data?.ctx?.property?.address?.municipality;
  const hasStreet = !!data?.ctx?.property?.address?.street;

  // Fetch the decision scenarios once and share them with the alternative
  // cards below. The hero uses only the top-pertinence one.
  const caseId = data?.ctx?.case_id;
  const decision = decisionEarly;

  // Split into a capital (sale) block and a rental potential block.
  const primaryIsRent = _isRentUnit(result.unit);
  const saleResult = primaryIsRent ? companion : result;
  const rentResult = primaryIsRent ? result : companion;
  const saleShown = !!saleResult && saleResult.absolute_value_central != null;
  const rentShown = !!rentResult && rentResult.absolute_value_central != null;

  // Yield estimate (annual gross), when both blocks are numeric.
  let annualYieldPct = null;
  // Post-collaudo · denominatore = stesso valore mostrato all'utente (attuale, corretto per lo stato).
  const _yieldDen = saleResult?.condition_adjustment?.current_absolute_value_central ?? saleResult?.absolute_value_central;
  if (saleShown && rentShown
      && Number.isFinite(_yieldDen)
      && Number.isFinite(rentResult.absolute_value_central)
      && _yieldDen > 0) {
    annualYieldPct = (rentResult.absolute_value_central * 12
                      / _yieldDen) * 100;
  }

  // Compute the hero from the top-pertinence scenario. Falls back to
  // the raw commercial value when scenarios are still loading (shown
  // as a loading placeholder) or when the top scenario is sale-side.
  const saleValueCentral = saleShown ? saleResult.absolute_value_central : null;
  // V3 · Ciclo 2 · con la Richiesta strutturata l'hero resta il valore di VALUTA: i numeri delle operazioni
  // arrivano solo dal motore (pannello operazioni), senza regole di presentazione lato client.
  const hero = decision.isLoading
    ? null
    : pickHeroFromDecision(decision.view?.structured_request_ref ? null : decision.scenarios, saleValueCentral);

  return (
    <>
      <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-4"
         data-testid="valuta-result-status">
        Valuta · Indicazione di mercato
      </p>
      <h1 className="font-display font-semibold text-3xl md:text-4xl leading-tight mb-2"
          data-testid="valuta-result-title">
        Il valore indicativo del tuo immobile
      </h1>
      {data?.ctx?.property?.address?.municipality && (
        <p className="text-vg2-muted mb-2" data-testid="valuta-result-municipality">
          {addressLine(data.ctx.property.address)}
        </p>
      )}
      <OfficialZoneLine geo={analysis?.geo_state} municipality={data?.ctx?.property?.address?.municipality} address={data?.ctx?.property?.address} />

      <ConfidenceIndicator level={result.confidence_level} />

      {/* §Phase 2A.6 · Zone narrative — read the qualitative context
          before the number. Non-blocking (hidden when absent). */}
      <ZoneNarrativeBlock
        narrative={result.zone_narrative}
        municipality={data?.ctx?.property?.address?.municipality}
      />

      {/* Hero — driven by the top-pertinence EVA³ scenario.
          - loading: explicit placeholder ("Sto individuando…")
          - sale-mode: preserve historical CapitalValueBlock behaviour
          - rent-mode: rent hero + contextual capital line below */}
      {hero === null ? (
        <DecisionHeroLoading />
      ) : hero.mode === "rent" ? (
        <>
          <RentHeroBlock hero={hero} caseId={caseId} analysisId={analysisId} />
          <ContextualCapitalLine
            saleValue={saleValueCentral}
            sourceScenarioTitle={hero.sourceScenarioTitle}
          />
        </>
      ) : hero.mode === "rent-insufficient" ? (
        <>
          <RentInsufficientBlock activity={hero.activity} />
          <ContextualCapitalLine
            saleValue={saleValueCentral}
            sourceScenarioTitle={hero.sourceScenarioTitle}
          />
        </>
      ) : saleShown ? (
        <CapitalValueBlock r={saleResult} data-testid-prefix="valuta-capital" />
      ) : primaryIsRent ? (
        <NoCapitalHint />
      ) : null}

      {/* §CEO-PRES-UI · Sintesi economica (3 letture principali) — sempre
          mostrata quando i dati esistono, come lettura iniziale organizzata. */}
      <EconomicSummaryBlock saleResult={saleResult}
                            rentResult={rentResult}
                            annualYieldPct={annualYieldPct} />

      {/* Rental potential (rent) — always rendered when applicable, but
          only when it isn't already the hero (avoid double-hero). */}
      {/* Rental potential (ordinary long-term) — only as supplementary info
          when the hero is NOT already an income operation. Never shown for a
          declared income operation (casa vacanze / studenti / reddito), per
          §BUGFIX-OBJ: non mostrare il canone ordinario quando l'utente ha
          dichiarato un'altra destinazione economica. */}
      {rentShown && hero?.mode !== "rent" && hero?.mode !== "rent-insufficient" && (
        <RentPotentialBlock r={rentResult}
                            annualYieldPct={annualYieldPct}
                            data-testid-prefix="valuta-rent" />
      )}

      {effectiveObjectiveMeta && (
        <ObjectivePanel objectiveMeta={effectiveObjectiveMeta} data={data}
                        showDisclaimer={!!projectMeta} />
      )}

      <FactorsBlock adjustments={result.adjustments || []} />

      <ValutaRangeChart primary={result} companion={companion} companionMode={companionMode} />
      <MercatoIntornoBlock caseId={data?.ctx?.case_id}
                           analysisId={analysis?.analysis_id} />
      <ValutaObjectiveImpact objectiveMeta={effectiveObjectiveMeta} result={result} companion={companion} companionMode={companionMode} hero={hero} />

      {/* §M8.4 · Rimossi ValutaEvidenceComposition e SourcesBlock:
          gergo interno (classificazione di supporto, official_territorial,
          codici zona OMI, semestri 2025-07→2025-12) non deve essere
          esposto all'utente. Il canonical disclaimer resta in EconomicBlock
          e nelle sezioni economiche. */}

      <RuleRefreshNotice result={saleResult} />
      <ValuationMethodDetails trace={saleResult?.calculation_trace} declaredCondition={projectUnderstanding?.declared_condition} />

      {decision.view?.structured_request_ref ? null : (
        <AlternativesInline caseId={data.ctx?.case_id}
                            analysisId={analysisId}
                            decision={decision} />
      )}

      {!canProceedToDecidi && <NoDecidiHint />}

      {/* §CEO-PRES-UI (GO parziale) · Accesso esplicito a DECIDI dopo le
          alternative. Unico invito a proseguire. */}
      {canProceedToDecidi && <GoToDecidiSection onDecidi={onDecidi} structured={!!decision.view?.structured_request_ref} />}
    </>
  );
}


function CapitalValueBlock({ r }) {
  const ca = r.condition_adjustment || null;
  const adjusted = !!ca && ca.applied
    && ca.current_absolute_value_central != null;
  // Valore mostrato: "così com'è" corretto per lo stato (coerente con gli
  // scenari). La base integrata OMI+web resta visibile sotto.
  const display = adjusted
    ? {
        absolute_value_low: ca.current_absolute_value_low,
        absolute_value_central: ca.current_absolute_value_central,
        absolute_value_high: ca.current_absolute_value_high,
        unit_value_low: ca.current_unit_value_low,
        unit_value_central: ca.current_unit_value_central,
        unit_value_high: ca.current_unit_value_high,
      }
    : r;
  return (
    <section className="mt-8 mb-8" data-testid="valuta-capital-block">
      <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-3">
        Valore attuale stimato
      </p>
      <p className="font-display font-semibold leading-none text-vg2-red"
         data-testid="valuta-capital-central">
        <span className="text-5xl md:text-6xl">{fmtEur(display.absolute_value_central)}</span>
      </p>
      {display.unit_value_central != null && (
        <p className="mt-3 text-vg2-muted" data-testid="valuta-capital-unit">
          ({fmtEur(display.unit_value_central)} /m²)
        </p>
      )}
      <RangeBar r={display} isRent={false} testidPrefix="valuta-capital" />
    </section>
  );
}


function MarketIntegrationSources({ integ }) {
  if (!integ) return null;
  const blended = integ.method === "omi_web_blend_50_50";
  const ws = integ.web_source || null;
  return (
    <div className="mt-5 rounded-lg border border-vg2-line bg-vg2-paper/40 p-4"
         data-testid="valuta-market-integration">
      <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-2">
        Fonti del valore di mercato
      </p>
      {blended ? (
        <p className="text-sm text-vg2-ink" data-testid="valuta-integration-method">
          Valore iniziale integrato:{" "}
          <span className="font-semibold">media 50% OMI + 50% dato web</span>.
        </p>
      ) : (
        <p className="text-sm text-vg2-ink" data-testid="valuta-integration-method">
          Valore basato <span className="font-semibold">solo su OMI</span>{" "}
          (nessun dato web affidabile o confrontabile).
        </p>
      )}
      <ul className="mt-2 space-y-1 text-sm text-vg2-muted">
        {integ.omi_unit_value != null && (
          <li data-testid="valuta-integration-omi">
            OMI: {fmtEur(integ.omi_unit_value)} /m²
          </li>
        )}
        {integ.web_unit_value != null && ws && (
          <li data-testid="valuta-integration-web">
            {(ws.source_name === "idealista" ? "Idealista"
              : ws.source_name === "immobiliare" ? "Immobiliare.it"
              : ws.source_name)}
            {ws.geo_level === "zone" ? " (zona)" : " (comune)"}:{" "}
            {fmtEur(integ.web_unit_value)} /m²
            {ws.period_declared ? ` · ${ws.period_declared}` : ""}
            {ws.source_url && (
              <>
                {" · "}
                <a href={ws.source_url} target="_blank" rel="noopener noreferrer"
                   className="underline hover:text-vg2-red"
                   data-testid="valuta-integration-web-link">
                  fonte
                </a>
              </>
            )}
          </li>
        )}
        {blended && integ.blended_unit_value != null && (
          <li className="text-vg2-ink font-medium"
              data-testid="valuta-integration-blended">
            Base integrata: {fmtEur(integ.blended_unit_value)} /m²
            {" "}(prima degli adeguamenti di stato)
          </li>
        )}
      </ul>
    </div>
  );
}


function RentPotentialBlock({ r, annualYieldPct }) {
  return (
    <section className="mt-4 mb-10 border-t border-vg2-line pt-6"
             data-testid="valuta-rent-block">
      <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-3">
        Quanto potresti ricavare dall'affitto
      </p>
      <p className="font-display font-semibold leading-none text-vg2-red"
         data-testid="valuta-rent-central">
        <span className="text-4xl md:text-5xl">{fmtEur(r.absolute_value_central)}</span>
        <span className="text-xl text-vg2-muted font-normal"> /mese</span>
      </p>
      {r.unit_value_central != null && (
        <p className="mt-3 text-vg2-muted" data-testid="valuta-rent-unit">
          ({fmtEur(r.unit_value_central)} /m²/mese)
        </p>
      )}
      <p className="mt-2 text-sm text-vg2-muted" data-testid="valuta-rent-subtitle">
        Stima indicativa per una locazione ordinaria.
      </p>
      <RangeBar r={r} isRent={true} testidPrefix="valuta-rent" />
      {annualYieldPct != null && (
        <p className="mt-3 text-sm text-vg2-ink"
           data-testid="valuta-rent-yield">
          Rendimento lordo indicativo:{" "}
          <span className="font-semibold">
            {annualYieldPct.toFixed(1).replace(".", ",")}%
          </span>{" "}
          annuo — al lordo di costi, imposte e periodi di sfitto.
        </p>
      )}
    </section>
  );
}


function NoCapitalHint() {
  return (
    <section className="mt-6 mb-8" data-testid="valuta-capital-missing">
      <p className="text-sm text-vg2-muted max-w-[620px]">
        Il valore capitale dell’immobile verrà mostrato non appena avremo
        le fonti di compravendita sufficienti per questa microzona.
      </p>
    </section>
  );
}


function NoDecidiHint() {
  return (
    <section className="border-t border-vg2-line pt-8 mt-4"
             data-testid="valuta-result-no-decidi">
      <p className="text-sm text-vg2-muted max-w-[620px]"
         data-testid="valuta-result-no-decidi-lede">
        Confronteremo le strade possibili in DECIDI non appena avremo
        l’ancora territoriale precisa. Aggiungi il dato mancante qui
        sopra per procedere.
      </p>
    </section>
  );
}


// ------------------------------------------------------------- Fallback view

function FallbackView({ result, data, objectiveCode, canProceedToDecidi, onDecidi }) {
  const hasMunicipality = !!data?.ctx?.property?.address?.municipality;
  const hasStreet = !!data?.ctx?.property?.address?.street;
  const localized = localizePrecisionActions(result.precision_improvement_actions, { context: { hasMunicipality, hasStreet } });
  const evidence = describeAvailableEvidence(result, data);
  const missing = describeMissingEvidence(result);
  return (
    <>
      <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-4"
         data-testid="valuta-result-status">
        Valuta · Inquadramento
      </p>
      <h1 className="font-display font-semibold text-3xl md:text-4xl leading-tight mb-3"
          data-testid="valuta-result-title">
        Abbiamo inquadrato l’immobile. Servono ancora alcuni dati per stimarne il valore.
      </h1>
      {data?.ctx?.property?.address?.municipality && (
        <p className="text-vg2-muted mb-8" data-testid="valuta-result-municipality">
          {addressLine(data.ctx.property.address)}
        </p>
      )}

      <ConfidenceIndicator level={result.confidence_level || "LOW"} />

      <section className="mt-8 mb-8" data-testid="valuta-result-fallback-evidence">
        <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-3">
          Cosa abbiamo raccolto finora
        </p>
        <ul className="space-y-2">
          {evidence.length === 0 ? (
            <li className="text-vg2-muted text-sm">
              Al momento non abbiamo raccolto evidenze territoriali utilizzabili.
            </li>
          ) : evidence.map((line, i) => (
            <li key={i} className="text-vg2-ink text-sm"
                data-testid={`fallback-evidence-${i}`}>• {line}</li>
          ))}
        </ul>
      </section>

      <section className="mb-8" data-testid="valuta-result-fallback-missing">
        <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-3">
          Cosa manca per una stima numerica
        </p>
        <ul className="space-y-2">
          {missing.map((line, i) => (
            <li key={i} className="text-vg2-ink text-sm"
                data-testid={`fallback-missing-${i}`}>• {line}</li>
          ))}
        </ul>
      </section>

      {localized.length > 0 && (
        <section className="mb-8" data-testid="valuta-result-fallback-actions">
          <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-3">
            Come procedere
          </p>
          <ul className="space-y-2">
            {localized.map((line, i) => (
              <li key={i} className="text-vg2-ink text-sm"
                  data-testid={`fallback-action-${i}`}>• {line}</li>
            ))}
          </ul>
        </section>
      )}

      <ObjectiveHintFallback objectiveCode={objectiveCode} />

      {canProceedToDecidi
        ? <DecidiCta onDecidi={onDecidi} label="Continua con un orientamento non numerico" />
        : <NoDecidiHint />
      }
    </>
  );
}


// ---------------------------------------------------------------- Sub-blocks

function ConfidenceIndicator({ level }) {
  const dots = CONFIDENCE_DOTS[level] || 1;
  const label = CONFIDENCE_LABEL_IT[level] || CONFIDENCE_LABEL_IT.LOW;
  return (
    <div data-testid="valuta-result-confidence" className="flex items-center gap-3">
      <div className="flex gap-1" aria-hidden="true">
        {[1, 2, 3].map((d) => (
          <span key={d}
                className={
                  "inline-block w-2.5 h-2.5 rounded-full " +
                  (d <= dots ? "bg-vg2-red" : "bg-vg2-line")
                } />
        ))}
      </div>
      <p className="text-sm" data-testid="valuta-result-confidence-label">
        {label} · {dots}/3
      </p>
    </div>
  );
}


// Zona Mostrata · la zona ufficiale OMI usata per la stima, così il proprietario può verificarla.
function OfficialZoneLine({ geo, municipality, address }) {
  const label = (geo?.omi_zone_label || "").split(" / ")[1];
  const street = (address?.street || "").trim();
  const zoneOnly = /^\s*(zona|quartiere|rione)\s+/i.test(street);
  const hint = !street || zoneOnly
    ? "Se non è la zona del tuo immobile, indica la via: la stima si aggiorna sulla zona corretta."
    : null;  // via presente: l'eventuale civico facoltativo è già proposto dall'avviso di precisione
  if (label) {
    const [code, ...rest] = label.split(" - ");
    return (
      <p className="text-sm text-vg2-ink mb-8" data-testid="valuta-result-official-zone">
        Zona ufficiale usata per la stima: <strong>{rest.join(" - ") || code}</strong> (zona OMI {code}{municipality ? `, ${municipality}` : ""}).
        {hint && <span className="block text-xs text-vg2-muted mt-0.5" data-testid="valuta-result-official-zone-hint">{hint}</span>}
      </p>
    );
  }
  if (geo?.status === "municipality_fallback") {
    return (
      <p className="text-sm text-vg2-ink mb-8" data-testid="valuta-result-official-zone">
        Nessuna zona ufficiale individuata: la stima usa il riferimento dell’intero comune{municipality ? ` di ${municipality}` : ""}.
      </p>
    );
  }
  return <div className="mb-6" />;
}

// §Phase 2A.6 · Zone narrative — qualitative context paragraphs about the
// OMI zone (history, vocation, points of interest, forecasts) generated
// by market_context_ai_service. Rendered above the capital block so the
// owner reads the context before the number. Non-blocking: gracefully
// hides when the backend did not produce a narrative for this case.
function ZoneNarrativeBlock({ narrative, municipality }) {
  const paragraphs = (typeof narrative === "string"
    ? narrative.split(/\n\s*\n/).map((p) => p.trim()).filter((p) => p.length > 0)
    : []);
  // §ZONE-INTRO-ALWAYS · l'introduzione di zona è garantita dal backend
  // (cache → ricerca → senza ricerca → testo deterministico). Se per
  // qualunque ragione manca, NON mostriamo alcun testo neutro: nascondiamo
  // la sezione (mai il vecchio "Non abbiamo raccolto…").
  if (paragraphs.length === 0) return null;
  return <ZoneNarrativeBody paragraphs={paragraphs} municipality={municipality} />;
}

function ZoneNarrativeBody({ paragraphs, municipality }) {
  const [open, setOpen] = React.useState(false);
  const shown = open ? paragraphs : paragraphs.slice(0, 1);
  return (
    <section
      className="mt-8 mb-10 border-l-4 border-vg2-red pl-5 py-2"
      data-testid="valuta-zone-narrative"
    >
      <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-3">
        Contesto della zona{municipality ? ` · ${municipality}` : ""}
      </p>
      <div className="space-y-3 max-w-[720px]">
        {shown.map((p, i) => (
          <p
            key={i}
            className="text-vg2-ink leading-relaxed"
            data-testid={`valuta-zone-narrative-p-${i + 1}`}
          >
            {p}
          </p>
        ))}
      </div>
      {paragraphs.length > 1 && (
        <button type="button" onClick={() => setOpen((v) => !v)} data-testid="valuta-zone-narrative-toggle"
                className="mt-2 text-sm text-vg2-muted underline hover:text-vg2-red transition-colors">
          {open ? "Mostra meno" : "Altre notizie sulla zona"}
        </button>
      )}
      <p className="text-xs text-vg2-muted mt-2" data-testid="valuta-zone-narrative-note">
        Testo descrittivo da fonti pubbliche, non verificato da ValutaGenio: non entra nel calcolo del valore.
      </p>
    </section>
  );
}



function RangeBar({ r, isRent, testidPrefix }) {
  const prefix = testidPrefix || "valuta-result";
  const low = r.absolute_value_low ?? r.unit_value_low;
  const central = r.absolute_value_central ?? r.unit_value_central;
  const high = r.absolute_value_high ?? r.unit_value_high;
  if (low == null || central == null || high == null || high <= low) return null;
  const suffix = r.absolute_value_low != null
    ? (isRent ? " /mese" : "")
    : (isRent ? " /m²/mese" : " /m²");
  const pct = (v) => Math.min(100, Math.max(0, ((v - low) / (high - low)) * 100));
  return (
    <div className="mt-6 mb-4" data-testid={`${prefix}-range`}>
      <div className="relative h-2 bg-vg2-line rounded-full" aria-hidden="true">
        <div className="absolute top-[-6px] w-1 h-5 bg-vg2-red rounded-full"
             style={{ left: `${pct(central)}%` }}
             data-testid={`${prefix}-range-central-tick`} />
      </div>
      <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
        <div>
          <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted">
            Fascia prudente
          </p>
          <p className="font-medium" data-testid={`${prefix}-low`}>
            {fmtEur(low)}{suffix}
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted">
            Valore centrale
          </p>
          <p className="font-semibold text-vg2-red"
             data-testid={`${prefix}-central-inline`}>
            {fmtEur(central)}{suffix}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted">
            Fascia alta
          </p>
          <p className="font-medium" data-testid={`${prefix}-high`}>
            {fmtEur(high)}{suffix}
          </p>
        </div>
      </div>
    </div>
  );
}


function ObjectivePanel({ objectiveMeta, data, showDisclaimer }) {
  const tf = (data?.ctx?.answers || {}).F_timeframe;
  const timeframeLabel = TIMEFRAME_COPY_IT[tf];
  return (
    <section className="mb-10 border-l-4 border-vg2-red pl-5 py-2"
             data-testid="valuta-result-objective">
      <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-2">
        Il valore rispetto al tuo obiettivo
      </p>
      <p className="text-lg font-medium mb-1" data-testid="valuta-result-objective-label">
        {objectiveMeta.label}
        {timeframeLabel && (
          <span className="text-vg2-muted font-normal"> · {timeframeLabel}</span>
        )}
      </p>
      <p className="text-vg2-ink text-sm leading-relaxed max-w-[620px]"
         data-testid="valuta-result-objective-lede">
        {objectiveMeta.lede}
      </p>
      {objectiveMeta.checkpoints && objectiveMeta.checkpoints.length > 0 && (
        <ul className="mt-3 space-y-1">
          {objectiveMeta.checkpoints.map((c, i) => (
            <li key={i} className="text-sm text-vg2-ink"
                data-testid={`valuta-result-checkpoint-${i}`}>
              <span className="text-vg2-red mr-2" aria-hidden="true">◆</span>{c}
            </li>
          ))}
        </ul>
      )}
      {showDisclaimer && (
        <p className="mt-4 text-xs text-vg2-muted leading-relaxed max-w-[620px]"
           data-testid="valuta-result-objective-disclaimer">
          {OFFICIAL_DISCLAIMER_IT}
        </p>
      )}
    </section>
  );
}


function ObjectiveHintFallback({ objectiveCode }) {
  const meta = OBJECTIVE_COPY_IT[objectiveCode];
  if (!meta) return null;
  return (
    <section className="mb-10 border-l-4 border-vg2-line pl-5 py-2"
             data-testid="valuta-result-objective-fallback">
      <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-2">
        Ancora utile rispetto al tuo obiettivo
      </p>
      <p className="text-lg font-medium mb-1">{meta.label}</p>
      <p className="text-vg2-ink text-sm leading-relaxed max-w-[620px]">
        Anche senza un numero preciso possiamo procedere a DECIDI, dove
        metteremo a confronto le strade compatibili con il tuo obiettivo
        e con lo stato attuale dell’immobile.
      </p>
    </section>
  );
}


function FactorsBlock({ adjustments }) {
  const applied = adjustments.filter((a) => Number.isFinite(a.numerical_effect_applied)
                                            && a.numerical_effect_applied !== 0);
  if (applied.length === 0) return null;
  const favorable = applied.filter((a) => a.numerical_effect_applied > 0);
  const penalizing = applied.filter((a) => a.numerical_effect_applied < 0);
  // Nota: la vecchia colonna "Da verificare" mostrava tutte le feature
  // dichiarate dall'utente (giardino, superficie, piano, ecc.) come se
  // fossero mancanti. In realtà UNVERIFIED indica solo che il coefficiente
  // di prezzo per quella feature non è ancora calibrato — non che il
  // dato del proprietario manchi. Rimossa per evitare l'ambiguità.
  return (
    <section className="mb-10" data-testid="valuta-result-factors">
      <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-3">
        Fattori che incidono sulla stima
      </p>
      <div className="grid gap-4 md:grid-cols-2">
        <FactorList title="Favorevoli" testid="factors-favorable"
                    items={favorable.map((a) => featureLabel(a.feature_name))} />
        <FactorList title="Penalizzanti" testid="factors-penalizing"
                    items={penalizing.map((a) => featureLabel(a.feature_name))} />
      </div>
    </section>
  );
}


function FactorList({ title, items, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-sm font-medium mb-2">{title}</p>
      {items.length === 0 ? (
        <p className="text-vg2-muted text-xs">Nessuno rilevato.</p>
      ) : (
        <ul className="space-y-1">
          {items.map((label, i) => (
            <li key={i} className="text-sm text-vg2-ink">• {label}</li>
          ))}
        </ul>
      )}
    </div>
  );
}


function DecidiCta({ onDecidi, label }) {
  // M8.4 · Primary CTA now scrolls to the inline Alternatives block so
  // the owner enters the M6.1 compare view instead of jumping to the
  // legacy DECIDI page. `onDecidi` is kept for the fallback (non-numeric)
  // case where the alternatives section may not be present.
  const goToCompare = React.useCallback(() => {
    const el = document.querySelector('[data-testid="valuta-alternatives"]');
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    if (typeof onDecidi === "function") onDecidi();
  }, [onDecidi]);

  return (
    <section className="border-t border-vg2-line pt-8 mt-4"
             data-testid="valuta-result-decidi-cta">
      <p className="text-lg font-medium mb-2"
         data-testid="valuta-result-decidi-lede">
        Ora mettiamo in relazione il valore, il tuo obiettivo e ciò che
        serve per procedere.
      </p>
      <button type="button"
              onClick={goToCompare}
              data-testid="valuta-result-goto-decidi"
              className="mt-4 inline-flex items-center gap-2 px-6 py-3 bg-vg2-red text-white rounded-full hover:bg-vg2-red-hover transition-colors font-semibold">
        {label || "Metti a confronto le strade possibili →"}
      </button>
    </section>
  );
}


// ------------------------------------------------------------------ helpers

function addressLine(address) {
  if (!address) return "";
  const parts = [];
  if (address.street) parts.push(address.street);
  if (address.municipality) parts.push(address.municipality);
  return parts.join(", ");
}


const _FEATURE_LABEL_IT = {
  surface_sqm: "Superficie",
  floor: "Piano",
  floors_total: "Numero piani edificio",
  elevator: "Ascensore",
  condition: "Stato conservativo",
  bathrooms: "Bagni",
  bedrooms: "Camere",
  rooms: "Numero locali",
  balcony: "Balcone",
  terrace: "Terrazzo",
  garden: "Giardino",
  parking: "Posto auto",
  box: "Box auto",
  cellar: "Cantina",
  attic: "Soffitta",
  heating: "Riscaldamento",
  energy_class: "Classe energetica",
  exposure: "Esposizione",
  brightness: "Luminosità",
  view: "Vista",
  occupancy: "Stato di occupazione",
  year_built: "Anno di costruzione",
  zone: "Zona / microzona",
};

function featureLabel(name) {
  return _FEATURE_LABEL_IT[name] || name;
}


function describeAvailableEvidence(result, data) {
  const lines = [];
  const address = data?.ctx?.property?.address;
  if (address?.municipality) {
    lines.push(`Comune riconosciuto: ${address.municipality}`);
  }
  const features = data?.ctx?.property?.features || {};
  if (features.surface_sqm) {
    lines.push(`Superficie dichiarata: ${features.surface_sqm} m²`);
  }
  if (features.condition) {
    lines.push(`Stato conservativo dichiarato`);
  }
  if ((result.anchors || []).length > 0) {
    lines.push(`Riferimenti territoriali disponibili: ${(result.anchors || []).length}`);
  }
  return lines;
}


function describeMissingEvidence(result) {
  const missing = (result.evidence_sources_missing || []).slice();
  const out = [];
  for (const raw of missing) {
    const [family, reason] = String(raw).split(":");
    if (family === "omi" && reason === "no_data") {
      out.push("Nessun dato OMI disponibile per questa microzona");
    } else if (family === "asking_market") {
      out.push("Prezzi richiesti sul mercato non ancora consultabili");
    } else if (family === "declared_transactions") {
      out.push("Compravendite dichiarate non ancora consultabili");
    }
  }
  const fallback = result.fallback_level;
  if (fallback === "REGION" || fallback === "NATIONAL") {
    out.push("Il livello territoriale disponibile è troppo ampio per una stima puntuale");
  }
  if (out.length === 0) {
    out.push("Servono più dati locali per stimare un valore puntuale");
  }
  return out;
}


// ------------------------------------------------------------------------
// §5 · Alternatives with scores + semaphores — VALUTA-ONLY
// (never in CAPISCI, DECIDI, AGISCI, email or dossier)
// ------------------------------------------------------------------------

const _SEMAPHORE_COPY = {
  RED: { label: "Guadagno negativo", bg: "bg-red-50", dot: "bg-red-500", txt: "text-red-700" },
  AMBER: { label: "Guadagno sotto soglia", bg: "bg-amber-50", dot: "bg-amber-500", txt: "text-amber-700" },
  YELLOW: { label: "Guadagno sotto soglia", bg: "bg-amber-50", dot: "bg-amber-500", txt: "text-amber-700" },
  GREEN: { label: "Strada forte", bg: "bg-emerald-50", dot: "bg-emerald-500", txt: "text-emerald-700" },
  DA_CHIARIRE: { label: "Da chiarire", bg: "bg-neutral-50", dot: "bg-neutral-400", txt: "text-neutral-700" },
};

// §VALUTA-CARDS (Giu 2026) · In questa fase le schede scenario mostrano
// SOLO tre elementi: voto numerico, semaforo e una frase che spiega il
// giudizio. Nessun dato economico (prezzo/canone/ricavo/rendimento).
// Semaforo qualitativo (niente riferimenti al "guadagno").
export const _SEM_BADGE = {
  GREEN: { dot: "bg-emerald-500", txt: "text-emerald-700", label: "Scelta solida" },
  AMBER: { dot: "bg-amber-500", txt: "text-amber-700", label: "Percorribile con verifiche" },
  YELLOW: { dot: "bg-amber-500", txt: "text-amber-700", label: "Percorribile con verifiche" },
  RED: { dot: "bg-red-500", txt: "text-red-700", label: "Poco adatta ora" },
  DA_CHIARIRE: { dot: "bg-neutral-400", txt: "text-neutral-600", label: "Da chiarire" },
};

const _VERDICT_SUFFIX = {
  GREEN: "una strada solida per questo immobile.",
  AMBER: "una strada percorribile, con le verifiche indicate.",
  YELLOW: "una strada percorribile, con le verifiche indicate.",
  RED: "una strada poco adatta in questa fase.",
  DA_CHIARIRE: "uno scenario da approfondire prima di procedere.",
};
const _FIT_POS = {
  zona: "si adatta bene a questa zona",
  immobile: "valorizza le caratteristiche dell'immobile",
  mercato: "è sostenuta dal momento di mercato",
};
const _FIT_NEG = {
  zona: "la zona incide meno su questa scelta",
  immobile: "l'immobile lascia meno margine",
  mercato: "il segnale di mercato di zona resta debole",
};

// Logica intrinseca di ciascuno scenario (non economica): descrive cosa
// fa quella strada, così la frase è specifica per lo scenario e non
// identica tra card con profilo di coerenza simile. Deriva dall'identità
// dello scenario prodotto dal sistema, non è inventata.
const _STRATEGY_LOGIC = {
  SELL_NOW: "libera subito il capitale senza interventi",
  SELL_AS_IS: "libera subito il capitale senza interventi",
  PREPARE_THEN_SELL: "cura la presentazione per vendere meglio",
  RENOVATE_THEN_SELL: "interviene sull'immobile prima di venderlo",
  LONG_TERM_RENT: "punta su un reddito stabile nel tempo",
  SHORT_TERM_RENT: "sfrutta la domanda per soggiorni brevi",
  STUDENT_RENT: "intercetta la domanda di affitti per studenti",
  HOLD: "mantiene l'immobile puntando sulla rivalutazione",
  RENOVATE_THEN_LONG_TERM_RENT: "ristruttura e poi mette a reddito",
  RENOVATE_THEN_SHORT_TERM_RENT: "esegue i lavori e poi sfrutta la domanda per soggiorni brevi",
  SPLIT_AND_SELL: "fraziona l'immobile per venderne le parti",
};

const _cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const _lc = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);

// Defensive: never surface euro/percent figures in a card phrase, even
// if an upstream field accidentally carries them.
function _stripEconomic(s) {
  if (!s) return s;
  return s
    .replace(/\([^()]*[€%][^()]*\)/g, "")
    .replace(/[^.·,;:]*[€%][^.·,;:]*/g, "")
    .replace(/\s{2,}/g, " ")
    .replace(/\s+([.,;:])/g, "$1")
    .trim();
}

// Parse the per-scenario, NON-economic coherence signals the engine
// stores in `score_rationale` ("Coerenza con la zona: 75/100 ·
// con l'immobile: 80/100 · con il segnale di mercato: 30/100").
function _parseFit(rationale) {
  if (!rationale) return null;
  const m = rationale.match(/zona:\s*(\d+).*?immobile:\s*(\d+).*?mercato:\s*(\d+)/i);
  if (!m) return null;
  return { zona: +m[1], immobile: +m[2], mercato: +m[3] };
}

// Build the short explanatory phrase shown on each scenario card.
// Derived from the real judgment produced by the system (fit profile /
// material supports / semaphore). Scenario-specific, never economic.
export function buildVerdictPhrase(card) {
  const state = card?.semaphore?.state || "DA_CHIARIRE";
  const suffix = _VERDICT_SUFFIX[state] || _VERDICT_SUFFIX.DA_CHIARIRE;

  // Clausola intrinseca dello scenario — rende la frase specifica per
  // quella strada (non economica).
  const key = String(card?.scenario_key || "").toUpperCase();
  const logic = _STRATEGY_LOGIC[key]
    || (card?.title ? _lc(card.title) : null);

  // Elemento di merito, in ordine di preferenza:
  // 1) un supporto concreto prodotto dal motore (quando presente);
  // 2) il profilo di coerenza (zona/immobile/mercato), sempre disponibile.
  let nuance = null;
  const sup = (card?.material_supports || []).map(_stripEconomic).find(Boolean);
  if (sup) {
    nuance = _lc(sup.replace(/\.$/, ""));
  } else {
    const fit = _parseFit(card?.score_rationale);
    if (fit) {
      const ranked = [
        ["zona", fit.zona], ["immobile", fit.immobile], ["mercato", fit.mercato],
      ].sort((a, b) => b[1] - a[1]);
      const [sk, sv] = ranked[0];
      const [wk, wv] = ranked[ranked.length - 1];
      if (sv - wv >= 10) {
        nuance = _FIT_POS[sk];
        if (wv < 55) nuance += `, mentre ${_FIT_NEG[wk]}`;
      } else {
        nuance = "con un buon equilibrio tra zona, immobile e mercato";
      }
    }
  }

  if (logic && nuance) return `${_cap(logic)}; ${nuance}: ${suffix}`;
  if (logic) return `${_cap(logic)}: ${suffix}`;
  if (nuance) return `${_cap(nuance)}: ${suffix}`;

  const acc = _stripEconomic(card?.semaphore?.accessible_label);
  if (acc) return acc;
  return _cap(suffix);
}

// Voto + semaforo + frase — the only three elements a scenario card
// may show in this phase.
function ScenarioVerdict({ card, testidPrefix, caseId, analysisId }) {
  const state = card?.semaphore?.state || "DA_CHIARIRE";
  const badge = _SEM_BADGE[state] || _SEM_BADGE.DA_CHIARIRE;
  const phrase = buildVerdictPhrase(card);
  const deepDiveHref = (caseId && analysisId && card?.scenario_key)
    ? `/vg2/valuta/scenario/${caseId}/${analysisId}/${card.scenario_key}`
    : null;
  return (
    <div className="mt-3 pt-3 border-t border-vg2-line/60">
      <div
        className="inline-flex items-center gap-2 mb-2"
        data-testid={`${testidPrefix}-semaphore`}
        aria-label={card?.semaphore?.accessible_label || badge.label}
      >
        <span className={"w-2.5 h-2.5 rounded-full " + badge.dot} aria-hidden />
        <span className={"text-xs font-medium " + badge.txt}>{badge.label}</span>
      </div>
      <p
        className="text-sm text-vg2-ink leading-relaxed"
        data-testid={`${testidPrefix}-rationale`}
      >
        {phrase}
      </p>
      {deepDiveHref && (
        <a
          href={deepDiveHref}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`${testidPrefix}-deepdive-link`}
          className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-vg2-red text-white text-sm font-semibold hover:bg-vg2-red-hover transition-colors"
        >
          ANALIZZA QUESTO SCENARIO <span aria-hidden>↗</span>
        </a>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// M6.2.1 · Vocation chips — small informative badges rendered above the
// alternative cards. The tags come from the backend (derived by
// eva3_context_builders._derive_vocation_tags from the M2 zone narrative)
// and make the "why here" transparent to the owner (e.g. "Zona centro
// storico", "Zona turistica").
// ---------------------------------------------------------------------------

const VOCATION_CHIP_LABELS = {
  centro_storico: "Zona centro storico",
  turistica: "Zona turistica",
  universitaria: "Zona universitaria",
  vip_pregio: "Zona di pregio",
  lacustre: "Zona lacustre",
  costiera: "Zona costiera",
  residenziale_periferica: "Zona residenziale periferica",
  in_dismissione: "Zona in trasformazione",
};

function VocationChips({ tags }) {
  if (!tags || tags.length === 0) return null;
  return (
    <div
      className="flex flex-wrap gap-2 mb-4"
      data-testid="valuta-vocation-chips"
    >
      {tags.map((t) => (
        <span
          key={t}
          data-testid={`valuta-vocation-chip-${t}`}
          className="inline-flex items-center px-3 py-1 rounded-full
                     text-xs font-medium tracking-tight
                     bg-vg2-red/8 text-vg2-red border border-vg2-red/20"
        >
          {VOCATION_CHIP_LABELS[t] || t}
        </span>
      ))}
    </div>
  );
}


// §LAUNCH-A · lettura ripetuta (solo GET) della decisione mentre la
// generazione — avviata ma interrotta dal timeout del gateway (~60s) — viene
// completata e PERSISTITA lato server. Nessuna nuova POST di generazione
// (nessun secondo costo AI). Attesa massima ~150s.
async function pollInlineDecision(getDecision, caseId, isCancelled) {
  // Attesa massima ~180s: su una zona MAI vista il backend esegue due
  // ricerche AI in parallelo (stima casa-vacanze + canone stanze) che
  // possono richiedere 60-90s complessivi; la generazione prosegue e viene
  // persistita lato server anche dopo il 502 del gateway (nessuna nuova POST,
  // nessun costo AI aggiuntivo).
  const deadline = Date.now() + 180000;
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, 5000));
    if (isCancelled()) return null;
    try {
      const v = await getDecision(caseId);
      if ((v?.scenarios || []).length) return v;
    } catch (_) { /* 404 → non ancora pronta, continua a leggere */ }
  }
  return null;
}

// --- Shared hook: fetch (or generate + fetch) the EVA³ decision once
// and share the scenarios between the hero and the alternative cards.
// Passing `null` as caseId short-circuits (used by children that receive
// the state from a parent).
function useDecisionScenarios(caseId) {
  const [scenarios, setScenarios] = React.useState(null);
  const [asteriskNote, setAsteriskNote] = React.useState(null);
  const [vocationTags, setVocationTags] = React.useState([]);
  const [decisionResultId, setDecisionResultId] = React.useState(null);
  const [primaryScenarioKey, setPrimaryScenarioKey] = React.useState(null);
  const [ownerSelectedKey, setOwnerSelectedKey] = React.useState(null);
  const [view, setView] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!caseId) return;
    let cancelled = false;
    (async () => {
      try {
        const { getDecision, generateDecision } = await import("@/lib/vg2Decidi");
        let view = null;
        try { view = await getDecision(caseId); } catch (_) { /* not generated yet */ }
        if (!view || !(view.scenarios || []).length || view.structured_request_ref?.stale) {
          try {
            await generateDecision(caseId, `d-inline-${caseId}`);
            view = await getDecision(caseId);
          } catch (e) {
            // §LAUNCH-A · timeout del gateway: la generazione prosegue e
            // viene persistita lato server. NON rilanciamo la POST: leggiamo
            // con sole GET finché la decisione compare (entro ~150s).
            const gs = e?.response?.status;
            const gatewayTimeout = !e?.response || gs === 502 || gs === 503
              || gs === 504 || e?.code === "ECONNABORTED";
            if (gatewayTimeout) {
              view = await pollInlineDecision(getDecision, caseId, () => cancelled);
            }
            if (!view) { if (!cancelled) setError(true); return; }
          }
        }
        if (cancelled) return;
        setScenarios(view?.scenarios || []);
        setAsteriskNote(view?.universal_asterisk_note || null);
        setVocationTags(view?.zone_vocation_tags || []);
        setDecisionResultId(view?.decision_result_id || null);
        setPrimaryScenarioKey(view?.primary_scenario_key || null);
        setOwnerSelectedKey(view?.owner_decision?.selected_scenario_key || null);
        setView(view);
      } catch (e) { if (!cancelled) setError(true); }
    })();
    return () => { cancelled = true; };
  }, [caseId]);

  return {
    scenarios,
    setScenarios,
    asteriskNote,
    vocationTags,
    decisionResultId,
    primaryScenarioKey,
    ownerSelectedKey,
    view,
    setView: (v) => { setView(v); setScenarios(v?.scenarios || []); },
    error,
    isLoading: !caseId ? false : (scenarios === null && !error),
  };
}


function AlternativesInline({ caseId, analysisId, decision }) {
  // Backward-compat: if no `decision` prop is passed (older callers),
  // we still self-fetch. The primary path is that NumericView already
  // fetched once and hands the state down to us — the hero and the
  // alternative cards then read the same source.
  const owned = useDecisionScenarios(decision ? null : caseId);
  const state = decision || owned;
  const {
    scenarios = null,
    setScenarios = null,
    vocationTags = [],
    decisionResultId = null,
    primaryScenarioKey = null,
    ownerSelectedKey = null,
    error: err = null,
  } = state || {};
  const [chosenKey, setChosenKey] = React.useState(ownerSelectedKey);
  React.useEffect(() => { setChosenKey(ownerSelectedKey); }, [ownerSelectedKey]);
  // M6.1 · comparison selection state — Set of scenario_key (max 2).
  const [selected, setSelected] = React.useState(new Set());
  const [compareOpen, setCompareOpen] = React.useState(false);
  const compareRef = React.useRef(null);
  // §Micro8 · stato "Analizza": una sola analisi per volta per caso.
  // analyzingKey = scenario in corso; interruptedKey = riga interrotta
  // (rete/limite di tempo) che mostra il messaggio + "Riprova".
  const [analyzingKey, setAnalyzingKey] = React.useState(null);
  const [interruptedKey, setInterruptedKey] = React.useState(null);

  const toggleSelect = React.useCallback((key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) { next.delete(key); return next; }
      if (next.size >= 2) return prev;  // max 2 · ignored click
      next.add(key);
      return next;
    });
  }, []);

  const clearSelection = React.useCallback(() => {
    setSelected(new Set());
    setCompareOpen(false);
  }, []);

  const handleCompareClick = React.useCallback(() => {
    setCompareOpen(true);
    // Smooth scroll to comparison section on next frame.
    setTimeout(() => {
      compareRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  }, []);

  // §Micro8 · click "Analizza" → chiama l'endpoint analyze (nessun timeout
  // client specifico). Una sola analisi per volta; al successo sostituisce
  // il segnaposto con la card completa aggiornando lo stato condiviso.
  const handleAnalyze = React.useCallback(async (scenarioKey) => {
    if (!caseId || analyzingKey) return;
    setInterruptedKey(null);
    setAnalyzingKey(scenarioKey);
    try {
      const { analyzeScenario } = await import("@/lib/vg2Decidi");
      const out = await analyzeScenario(caseId, scenarioKey);
      const next = out?.view?.scenarios;
      if (Array.isArray(next) && typeof setScenarios === "function") {
        setScenarios(next);
      }
    } catch (e) {
      // Interruzione (rete o limite di tempo del proxy): nessun
      // ritentativo automatico, messaggio neutro + "Riprova".
      setInterruptedKey(scenarioKey);
    } finally {
      setAnalyzingKey(null);
    }
  }, [caseId, analyzingKey, setScenarios]);

  if (err || (scenarios !== null && scenarios.length === 0)) return null;
  if (!scenarios) {
    return (
      <section className="mb-12" data-testid="valuta-alternatives-loading">
        <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-3">
          Alternative a confronto
        </p>
        <p className="text-sm text-vg2-muted">Sto confrontando le opzioni…</p>
      </section>
    );
  }
  // §Micro8 · card reali = non escluse (hero) e NON segnaposto. I segnaposto
  // (status "da_analizzare", applicabili, escluso student_rent) sono righe
  // leggere con pulsante "Analizza", mai card né semaforo.
  const { visible, placeholders } = partitionScenarios(scenarios);
                              // §CARD-EXCLUSION · lo scenario "puro" su
                              // cui è costruito il numero in cima (hero)
                              // resta nel payload per l'hero ma non è una
                              // card. Il backend applica già la selezione
                              // ottimi+buoni+1 contrasto (cap 6).
  if (visible.length === 0 && placeholders.length === 0) return null;
  const selectedCards = visible.filter((s) => selected.has(s.scenario_key));
  const canSelectMore = selected.size < 2;

  return (
    <section className="mb-12" data-testid="valuta-alternatives">
      <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-2">
        Alternative a confronto
      </p>
      {/* M6.2.1 · vocation chips — mostra a colpo d'occhio i tratti
          qualitativi della zona (derivati dal narrative di mercato) che
          motivano il segnale sulle strategie. */}
      <VocationChips tags={vocationTags} />
      {visible.length > 0 && (
        <>
          {/* §M8.14 · Frase fissa concordata con l'utente (Feb 2026):
              sostituisce ogni banner condizionale precedente. Il testo
              non afferma che non esistano scenari migliori, quindi resta
              vero anche se il sistema ne avesse mancato uno. */}
          <p
            className="text-base text-vg2-ink mb-4 leading-relaxed"
            data-testid="valuta-alt-standard-banner"
          >
            Le ricerche mostrano scenari alternativi con questo potenziale.
          </p>
          {/* §CEO-UX · ogni scheda ha l'azione principale «ANALIZZA QUESTO
              SCENARIO» che apre la VALUTA dedicata in una nuova scheda del
              browser; la VALUTA principale resta aperta e invariata. */}
          <p
            className="text-sm text-vg2-ink mb-4 leading-relaxed"
            data-testid="valuta-alt-compare-hint"
          >
            Apri «ANALIZZA QUESTO SCENARIO» per vedere costi, ricavi, margini,
            ipotesi e verifiche di ciascuna strategia in una nuova scheda.
          </p>
        </>
      )}
      <div className="space-y-3">
        {visible.map((s) => (
          <AlternativeCard
            key={s.scenario_key}
            card={s}
            caseId={caseId}
            analysisId={analysisId}
            decisionResultId={decisionResultId}
            primaryScenarioKey={primaryScenarioKey}
            chosenKey={chosenKey}
            onChoose={setChosenKey}
          />
        ))}
      </div>
      {/* §Micro8 · righe-segnaposto "da analizzare" (nessun semaforo/voto/
          numeri; non selezionabili). "Analizza" calcola lo scenario su
          richiesta e sostituisce la riga con la card completa. */}
      {placeholders.length > 0 && (
        <div className="space-y-3 mt-3" data-testid="valuta-alt-placeholders">
          {placeholders.map((s) => (
            <PlaceholderRow
              key={s.scenario_key}
              card={s}
              analyzing={analyzingKey === s.scenario_key}
              disabled={analyzingKey !== null
                        && analyzingKey !== s.scenario_key}
              interrupted={interruptedKey === s.scenario_key}
              onAnalyze={() => handleAnalyze(s.scenario_key)}
            />
          ))}
        </div>
      )}

    </section>
  );
}

// M6.1 · Sticky comparison bar — appears when 2 cards are selected and
// the comparison view is not open yet.
function CompareStickyBar({ selectedCount, onCompare, onClear, hidden }) {
  if (hidden) return null;
  return (
    <div
      className="fixed inset-x-0 bottom-0 z-40 bg-white/95 backdrop-blur border-t border-vg2-line shadow-[0_-4px_20px_rgba(0,0,0,0.06)]"
      data-testid="valuta-compare-sticky-bar"
    >
      <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
        <p className="text-sm text-vg2-ink">
          <span className="font-semibold">{selectedCount} strategie selezionate</span>
          <span className="text-vg2-muted hidden sm:inline"> · pronte per il confronto affiancato</span>
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClear}
            data-testid="valuta-compare-sticky-clear"
            className="text-sm text-vg2-muted underline underline-offset-2 hover:text-vg2-ink"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={onCompare}
            data-testid="valuta-compare-sticky-go"
            className="inline-flex items-center gap-2 px-4 py-2 bg-vg2-red text-white rounded-full hover:bg-vg2-red-hover text-sm font-semibold whitespace-nowrap"
          >
            Confronta le due strategie →
          </button>
        </div>
      </div>
    </div>
  );
}

// M6.1 · CompareCheckbox — 3-state indicator (empty / checked / disabled).
// Uses <button role="checkbox"> so it stays accessible without a real input.
function CompareCheckbox({ checked, disabled, onChange, scenarioKey }) {
  const title = disabled
    ? "Deseleziona un'altra strategia per abilitare questa"
    : checked
      ? "Rimuovi dal confronto"
      : "Aggiungi al confronto";
  const label = checked ? "Nel confronto" : "Confronta";
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-disabled={disabled}
      title={title}
      onClick={(e) => { e.stopPropagation(); if (!disabled) onChange(); }}
      data-testid={`valuta-alt-compare-checkbox-${scenarioKey}`}
      className={
        "inline-flex items-center gap-2 text-xs font-medium rounded-full border px-2.5 py-1 select-none transition-colors " +
        (checked
          ? "bg-vg2-red/10 border-vg2-red text-vg2-red hover:bg-vg2-red/15"
          : disabled
            ? "bg-neutral-50 border-neutral-200 text-neutral-400 cursor-not-allowed"
            : "bg-white border-vg2-line text-vg2-ink hover:border-vg2-red/60 hover:bg-vg2-red/5")
      }
    >
      <span
        aria-hidden
        className={
          "w-4 h-4 rounded-sm border flex items-center justify-center " +
          (checked
            ? "bg-vg2-red border-vg2-red"
            : disabled
              ? "bg-white border-neutral-300"
              : "bg-white border-vg2-line")
        }
      >
        {checked && (
          <svg viewBox="0 0 12 12" className="w-3 h-3 text-white" fill="none"
               stroke="currentColor" strokeWidth="2" strokeLinecap="round"
               strokeLinejoin="round">
            <polyline points="2.5 6.5 5 9 9.5 3.5" />
          </svg>
        )}
      </span>
      {label}
    </button>
  );
}

// M6.1 · CompareStrategiesView — inline side-by-side (desktop) / stack (mobile).
const CompareStrategiesView = React.forwardRef(function CompareStrategiesView(
  { cards, caseId, analysisId, onClose }, ref,
) {
  const [a, b] = cards;

  // §VALUTA-CARDS (Giu 2026) · la sintesi editoriale AI è disattivata in
  // questa fase: era fondata sui numeri economici (ricavo, canone, payback)
  // che non vogliamo mostrare, ed era una chiamata a un servizio a pagamento.
  // Il confronto resta disponibile su voto + semaforo + frase.

  return (
    <section
      ref={ref}
      className="mt-8 pt-6 border-t border-vg2-line"
      data-testid="valuta-compare-view"
    >
      <div className="flex items-baseline justify-between mb-4 gap-3">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-1">
            Confronto affiancato
          </p>
          <h3
            className="font-display text-2xl md:text-3xl text-vg2-ink leading-tight"
            data-testid="valuta-compare-title"
          >
            Due strategie a fianco per capire dove pende la scelta.
          </h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid="valuta-compare-close"
          className="text-sm text-vg2-muted underline underline-offset-2 hover:text-vg2-ink shrink-0"
        >
          Chiudi confronto
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <CompareColumn card={a} testid="valuta-compare-col-a" caseId={caseId} analysisId={analysisId} />
        <CompareColumn card={b} testid="valuta-compare-col-b" caseId={caseId} analysisId={analysisId} />
      </div>

      {/* M6.1 · PA hand-off CTA — leva primaria per l'utente indeciso. */}
      <div className="mt-8 rounded-lg bg-vg2-red/5 border border-vg2-red/20 p-5 md:p-6"
           data-testid="valuta-compare-pa-cta">
        <p className="font-display text-xl md:text-2xl text-vg2-ink leading-snug mb-2">
          Non riesci a decidere tra queste due?
        </p>
        <p className="text-sm text-vg2-muted max-w-[560px] mb-4 leading-relaxed">
          Un Property Advisor legge il quadro con te e aiuta a capire quale
          strada ha meno rischi per il tuo caso specifico.
        </p>
        <a
          href={caseId ? `/vg2/agisci/${caseId}` : "#"}
          data-testid="valuta-compare-pa-link"
          className="inline-flex items-center gap-2 px-5 py-3 bg-vg2-red text-white rounded-full hover:bg-vg2-red-hover font-semibold no-underline"
        >
          Chiedi il confronto gratuito con un Property Advisor →
        </a>
      </div>
    </section>
  );
});

// M8.2 · Editorial synthesis block rendered inside the compare view.
// Handles loading / ready / error states. Never blocks the view: if the
// LLM key is missing or the provider fails, the block just stays out of
// the way and the owner still sees the side-by-side numbers.
function CompareSynthesisBlock({ synth }) {
  if (!synth || synth.status === "idle") return null;
  if (synth.status === "loading") {
    return (
      <div
        className="mt-6 rounded-lg border border-vg2-line bg-white p-5 md:p-6"
        data-testid="valuta-compare-synthesis-loading"
      >
        <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-2">
          Sintesi in preparazione
        </p>
        <p className="text-sm text-vg2-muted italic">
          Sto ragionando sulle due opzioni rispetto al tuo obiettivo…
        </p>
      </div>
    );
  }
  if (synth.status === "error" || !synth.text) {
    // Graceful hide — no in-your-face error banner, just no synthesis.
    return null;
  }
  return (
    <div
      className="mt-6 rounded-lg border border-vg2-red/20 bg-vg2-subtle p-5 md:p-6"
      data-testid="valuta-compare-synthesis"
    >
      <p className="text-xs uppercase tracking-[0.14em] text-vg2-red mb-3 font-semibold">
        Sintesi · Cosa suggerisce il confronto
      </p>
      <p className="text-vg2-ink leading-relaxed max-w-[720px]"
         data-testid="valuta-compare-synthesis-text">
        {synth.text}
      </p>
    </div>
  );
}


// M6.1 · Single column of the comparison view — reuses card semantics with
// vertical layout so the two columns align field-by-field.
function CompareColumn({ card, testid, caseId, analysisId }) {
  const semState = card?.semaphore?.state || "DA_CHIARIRE";
  const sem = _SEMAPHORE_COPY[semState] || _SEMAPHORE_COPY.DA_CHIARIRE;
  const score = card.score;
  return (
    <article
      className={"border border-vg2-line rounded-lg p-4 " + sem.bg}
      data-testid={testid}
    >
      {/* Header — title + open-idea badge */}
      <div className="mb-3">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <p className="font-medium text-vg2-ink leading-snug"
             data-testid={`${testid}-title`}>
            {card.title}
          </p>
          {card.is_open_idea && (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-vg2-highlight/10 text-vg2-highlight border border-vg2-highlight/30"
              data-testid={`${testid}-open-idea-badge`}
            >
              Un'idea trovata per te
            </span>
          )}
        </div>
      </div>

      {/* Voto */}
      <div className="flex items-center justify-end gap-3 py-2 border-t border-vg2-line/60">
        {score != null && (
          <p className="font-display font-semibold text-2xl leading-none"
             data-testid={`${testid}-score`}>
            {score}<span className="text-sm text-vg2-muted font-normal">/100</span>
          </p>
        )}
      </div>

      {/* §VALUTA-CARDS · semaforo + frase, nessun dato economico. */}
      <ScenarioVerdict card={card} testidPrefix={testid}
                       caseId={caseId} analysisId={analysisId} />
    </article>
  );
}

const _fmtEurCompact = (v) => {
  if (v == null || isNaN(v)) return "—";
  return new Intl.NumberFormat("it-IT", {
    maximumFractionDigits: 0, useGrouping: "always",
  }).format(Math.round(v)) + " €";
};

// §CEO-PRES · se gli estremi coincidono mostro UN solo importo invece di
// "X € – X €" (banda degenere), coerente con l'etichetta tempi/payback.
const _fmtEurRange = (r) => {
  if (!r || r.low == null || r.high == null) return null;
  if (Math.round(r.low) === Math.round(r.high)) return _fmtEurCompact(r.low);
  return `${_fmtEurCompact(r.low)} – ${_fmtEurCompact(r.high)}`;
};

// §CEO-PRES · payback con una cifra decimale all'italiana (virgola) e
// banda degenere collassata a un valore.
const _fmtYears = (v) => {
  if (v == null || isNaN(v)) return null;
  return (Math.round(v * 10) / 10).toFixed(1).replace(".", ",");
};

export function EconomicBlock({ econ }) {
  if (!econ) return null;
  const rows = [];
  const push = (label, val) => {
    if (val) rows.push({ label, val });
  };
  // Labels come from the backend range.label when available so the
  // scenario context is preserved (e.g. "Costi preparazione + transazione"
  // for prepare_then_sell, "Costi transazione (APE + agenzia + IVA)" for
  // sell_as_is). Without this the user reads the raw total and thinks a
  // "light preparation" scenario costs like a full renovation.
  const _grossLabel = (econ.gross_proceeds_range?.label || "Valore vendita").trim();
  push(_grossLabel + "*", _fmtEurRange(econ.gross_proceeds_range));
  // §M8.14 · Split costi: se il backend ha inviato
  // ``preparation_costs_range`` + ``transaction_costs_range``, li
  // mostro come due voci distinte. Altrimenti fallback sulla vecchia
  // voce unica ``upfront_costs_range`` per retro-compatibilità.
  if (econ.preparation_costs_range && econ.transaction_costs_range) {
    const _prepLabel = (econ.preparation_costs_range.label || "Costi lavori").trim();
    const _txLabel = (econ.transaction_costs_range.label || "Costi di vendita").trim();
    push(_prepLabel + "*", _fmtEurRange(econ.preparation_costs_range));
    push(_txLabel + "*", _fmtEurRange(econ.transaction_costs_range));
  } else {
    const _upfrontLabel = (econ.upfront_costs_range?.label || "Costi").trim();
    push(_upfrontLabel + "*", _fmtEurRange(econ.upfront_costs_range));
  }
  push("Ricavo netto*", _fmtEurRange(econ.net_proceeds_range));
  push("Canone lordo/anno*", _fmtEurRange(econ.annual_gross_rent_range));
  push("Rendita netta/anno (stima parametrica)*", _fmtEurRange(econ.annual_net_rent_range));
  if (econ.payback_years_low != null && econ.payback_years_high != null) {
    const _pl = _fmtYears(econ.payback_years_low);
    const _ph = _fmtYears(econ.payback_years_high);
    push("Payback*", _pl === _ph ? `${_pl} anni` : `${_pl}-${_ph} anni`);
  }
  if (econ.time_to_execute_months) {
    const t = econ.time_to_execute_months;
    push("Tempo indicativo*",
      t.low === t.high ? `${t.low} mesi` : `${t.low}-${t.high} mesi`);
  }
  if (rows.length === 0 && (!econ.caveats || econ.caveats.length === 0)) {
    return null;
  }
  return (
    <div
      className="mt-3 pt-3 border-t border-vg2-line/60"
      data-testid="valuta-alt-economics"
    >
      {rows.length > 0 && (
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
          {rows.map((r, i) => (
            <div key={i} className="flex items-baseline justify-between gap-3">
              <dt className="text-vg2-muted">{r.label}</dt>
              <dd className="font-medium text-vg2-ink tabular-nums">{r.val}</dd>
            </div>
          ))}
        </dl>
      )}
      {econ.incremental_vs_sell_now && (() => {
        const inc = econ.incremental_vs_sell_now;
        const neg = inc.downside_negative;
        const _sign = (v) => (v > 0 ? "+" : "") + _fmtEurCompact(v);
        return (
          <div
            className={"mt-3 p-3 rounded-md border " + (neg
              ? "bg-amber-50 border-amber-300"
              : "bg-vg2-highlight/10 border-vg2-highlight/30")}
            data-testid="valuta-alt-incremental"
          >
            <p className="text-xs uppercase tracking-wider font-medium mb-2 text-vg2-ink">
              Confronto con la vendita immediata
            </p>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-vg2-muted">Incremento di valore*</dt>
                <dd className="font-medium text-vg2-ink tabular-nums">{_sign(inc.value_uplift_central)}</dd>
              </div>
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-vg2-muted">Investimento lavori*</dt>
                <dd className="font-medium text-vg2-ink tabular-nums">{_fmtEurCompact(inc.investment_central)}</dd>
              </div>
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-vg2-muted">Margine incrementale*</dt>
                <dd className={"font-semibold tabular-nums " + (inc.incremental_margin_central > 0 ? "text-emerald-700" : "text-red-700")}>
                  {_sign(inc.incremental_margin_central)}
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-vg2-muted">Scenario sfav.–fav.*</dt>
                <dd className="font-medium text-vg2-ink tabular-nums">
                  {_sign(inc.incremental_margin_low)} … {_sign(inc.incremental_margin_high)}
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-vg2-muted">Valore di pareggio*</dt>
                <dd className="font-medium text-vg2-ink tabular-nums">{_fmtEurCompact(inc.break_even_gross)}</dd>
              </div>
            </dl>
            {neg && (
              <p className="mt-2 text-xs text-amber-800 leading-relaxed" data-testid="valuta-alt-incremental-risk">
                Attenzione: nel caso sfavorevole il ricavo netto scende sotto la vendita
                immediata ({_fmtEurCompact(inc.baseline_net_sell_now)}). L'operazione comporta
                un rischio concreto di guadagno nullo o negativo.
              </p>
            )}
          </div>
        );
      })()}
      {econ.completeness === "partial" && (
        <p className="mt-2 text-xs text-vg2-muted italic">
          Analisi economica parziale — alcuni parametri richiedono verifica specifica.
        </p>
      )}
      {econ.feature_uplift_note && Math.abs(econ.feature_uplift_pct || 0) >= 0.1 && (
        <div
          className="mt-3 p-3 rounded-md bg-vg2-highlight/10 border border-vg2-highlight/30"
          data-testid="valuta-alt-feature-uplift"
        >
          <p className="text-xs uppercase tracking-wider text-vg2-highlight font-medium mb-1">
            Il tuo contesto aggiuntivo · {econ.feature_uplift_pct > 0 ? "+" : ""}
            {econ.feature_uplift_pct}%
          </p>
          <p className="text-xs text-vg2-ink leading-relaxed">
            {econ.feature_uplift_note}
          </p>
        </div>
      )}
      {econ.strategy_category === "short_term_rent" &&
       econ.gross_revenue_provenance?.source === "ai_research" ? (
        <div
          className="mt-3 p-2.5 rounded-md bg-amber-50 border border-amber-200"
          data-testid="valuta-alt-estimate-note"
        >
          <p className="text-xs text-amber-800 leading-relaxed">
            Scenario casa vacanze · valori annui indicativi. La rendita netta è
            una stima parametrica (una quota percentuale del ricavo lordo), non
            un utile netto calcolato sui costi effettivi. Da verificare con un
            Property Advisor.
          </p>
        </div>
      ) : econ.gross_revenue_provenance?.is_fallback && (
        <div
          className="mt-3 p-2.5 rounded-md bg-amber-50 border border-amber-200"
          data-testid="valuta-alt-estimate-note"
        >
          <p className="text-xs text-amber-800 leading-relaxed">
            Stima da confermare con dati reali di zona.
          </p>
        </div>
      )}
    </div>
  );
}

function PlaceholderRow({ card, analyzing, disabled, interrupted, onAnalyze }) {
  const key = card.scenario_key;
  return (
    <article
      data-testid={`valuta-alt-placeholder-${key}`}
      className="border border-dashed border-vg2-line rounded p-4 bg-white/60"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <p className="font-medium text-vg2-ink"
             data-testid={`valuta-alt-placeholder-title-${key}`}>
            {card.title}
          </p>
          {card.strategy_description && (
            <p className="text-sm text-vg2-muted mt-1 leading-relaxed">
              {card.strategy_description}
            </p>
          )}
        </div>
        <button
          type="button"
          disabled={analyzing || disabled}
          onClick={onAnalyze}
          data-testid={`valuta-alt-analyze-${key}`}
          className={
            "px-4 py-2 rounded-full text-sm font-medium transition-colors shrink-0 " +
            ((analyzing || disabled)
              ? "border border-vg2-line text-vg2-muted cursor-not-allowed"
              : "border border-vg2-red text-vg2-red hover:bg-vg2-red hover:text-white")
          }
        >
          {analyzing ? "Analisi in corso…" : "Analizza"}
        </button>
      </div>
      {analyzing && (
        <p className="mt-3 text-sm text-vg2-muted leading-relaxed"
           data-testid={`valuta-alt-analyzing-${key}`}>
          Stiamo analizzando questa strada. Può richiedere più di un minuto.
        </p>
      )}
      {!analyzing && interrupted && (
        <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm text-vg2-ink leading-relaxed max-w-[560px]"
             data-testid={`valuta-alt-analyze-interrupted-${key}`}>
            L'analisi sta richiedendo più tempo del previsto. Riprova tra
            qualche istante: se è già pronta la troverai subito.
          </p>
          <button
            type="button"
            disabled={disabled}
            onClick={onAnalyze}
            data-testid={`valuta-alt-analyze-retry-${key}`}
            className="px-4 py-2 rounded-full text-sm font-medium border border-vg2-red text-vg2-red hover:bg-vg2-red hover:text-white transition-colors shrink-0 disabled:opacity-50"
          >
            Riprova
          </button>
        </div>
      )}
    </article>
  );
}

function AlternativeCard({
  card, caseId, analysisId, decisionResultId, primaryScenarioKey, chosenKey, onChoose,
}) {
  const semState = card?.semaphore?.state || "DA_CHIARIRE";
  const sem = _SEMAPHORE_COPY[semState] || _SEMAPHORE_COPY.DA_CHIARIRE;
  const score = card.score;
  const navigate = useNavigate();
  const [submitting, setSubmitting] = React.useState(false);
  const [chooseErr, setChooseErr] = React.useState(null);
  const isChosen = chosenKey === card.scenario_key;

  const handleChoose = async () => {
    if (!caseId || !decisionResultId || submitting) return;
    setChooseErr(null);
    setSubmitting(true);
    try {
      const { postOwnerChoice, newChoiceOpId } = await import("@/lib/vg2Decidi");
      const choiceType = card.scenario_key === primaryScenarioKey
        ? "CONFIRM_PRIMARY" : "CHOOSE_ALTERNATIVE";
      const op = newChoiceOpId(caseId, choiceType, card.scenario_key);
      await postOwnerChoice(caseId, {
        decision_result_id: decisionResultId,
        choice_type: choiceType,
        selected_scenario_key: card.scenario_key,
        owner_reason: null,
        operation_id: op,
      });
      if (typeof onChoose === "function") onChoose(card.scenario_key);
      navigate(`/vg2/decidi/${caseId}`);
    } catch (e) {
      setChooseErr("Non riesco a registrare la scelta ora. Riprova.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <article
      data-testid={`valuta-alt-${card.scenario_key}`}
      className={
        "border rounded p-4 transition-colors border-vg2-line " +
        sem.bg
      }
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p
              className="font-medium text-vg2-ink"
              data-testid="valuta-alt-title"
            >
              {card.title}
            </p>
            {card.is_open_idea && (
              <span
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-vg2-highlight/10 text-vg2-highlight border border-vg2-highlight/30"
                data-testid="valuta-alt-open-idea-badge"
              >
                Un'idea trovata per te
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end min-w-[110px] gap-2">
          {score != null && (
            <p className="font-display font-semibold text-2xl leading-none"
               data-testid="valuta-alt-score">
              {score}<span className="text-sm text-vg2-muted font-normal">/100</span>
            </p>
          )}
        </div>
      </div>
      {/* §VALUTA-CARDS · solo voto (sopra) + semaforo + frase. Nessun dato
          economico né elenco di dettagli in questa fase. */}
      <ScenarioVerdict card={card} testidPrefix={`valuta-alt-${card.scenario_key}`}
                       caseId={caseId} analysisId={analysisId} />
      {caseId && decisionResultId && (
        <div className="mt-4 pt-3 border-t border-vg2-line/60 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs text-vg2-muted">
            Scegli questa strada per passare a DECIDI e vedere i
            documenti che ti servono.
          </p>
          <button
            type="button"
            disabled={submitting || isChosen}
            onClick={handleChoose}
            data-testid={`valuta-alt-choose-${card.scenario_key}`}
            className={
              "px-4 py-2 rounded-full text-sm font-medium transition-colors " +
              (isChosen
                ? "bg-emerald-500 text-white border border-emerald-500 cursor-default"
                : "border border-vg2-red text-vg2-red hover:bg-vg2-red hover:text-white")
            }
          >
            {isChosen ? "Scelta attuale ✓" : (submitting ? "Salvo…" : "Scegli questo scenario →")}
          </button>
        </div>
      )}
      {chooseErr && (
        <p role="alert" className="text-xs text-vg2-error mt-2"
           data-testid={`valuta-alt-choose-error-${card.scenario_key}`}>
          {chooseErr}
        </p>
      )}
    </article>
  );
}

// ---------------------------------------------------------------------------
// §Blocco 2 · Quattro grafici VALUTA richiesti dal mandato
// Ogni componente è data-driven dal payload dell'API di valutazione.
// ---------------------------------------------------------------------------

const _fmtEur = (v, digits = 0) => (v == null || isNaN(v))
  ? "—"
  : new Intl.NumberFormat("it-IT", { maximumFractionDigits: digits, useGrouping: "always" }).format(Math.round(v * (digits ? 100 : 1)) / (digits ? 100 : 1)) + " €";

function _rowRange({ label, low, central, high, unit, isRent, testid }) {
  if (low == null && central == null && high == null) return null;
  const min = Math.min(...[low, central, high].filter(Number.isFinite));
  const max = Math.max(...[low, central, high].filter(Number.isFinite));
  const pct = (v) => {
    if (max === min || !Number.isFinite(v)) return 50;
    return Math.round(((v - min) / (max - min)) * 100);
  };
  return (
    <div className="mb-4" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-1">
        <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted">{label}</p>
        <p className="text-sm text-vg2-muted">
          {isRent ? "€/m²/mese" : "€/m²"}
        </p>
      </div>
      <div className="relative h-2 bg-vg2-subtle rounded-full">
        {Number.isFinite(low) && Number.isFinite(high) && (
          <span className="absolute top-0 h-2 bg-vg2-line/60 rounded-full"
                style={{ left: `${pct(low)}%`, width: `${pct(high) - pct(low)}%` }} />
        )}
        {Number.isFinite(central) && (
          <span className="absolute top-1/2 -translate-y-1/2 w-2 h-4 bg-vg2-red rounded-sm"
                style={{ left: `calc(${pct(central)}% - 4px)` }} />
        )}
      </div>
      <div className="flex justify-between text-sm mt-2">
        <div className="flex flex-col items-start">
          <span>{isRent ? _fmtEur(low, 2) : _fmtEur(low)}</span>
          <span className="text-[10px] uppercase tracking-wider text-vg2-muted">
            Prudente
          </span>
        </div>
        <div className="flex flex-col items-center">
          <span className="font-medium text-vg2-red">
            {isRent ? _fmtEur(central, 2) : _fmtEur(central)}
          </span>
          <span className="text-[10px] uppercase tracking-wider text-vg2-muted">
            Centrale
          </span>
        </div>
        <div className="flex flex-col items-end">
          <span>{isRent ? _fmtEur(high, 2) : _fmtEur(high)}</span>
          <span className="text-[10px] uppercase tracking-wider text-vg2-muted">
            Alta
          </span>
        </div>
      </div>
    </div>
  );
}

function ValutaRangeChart({ primary, companion, companionMode }) {
  if (!primary) return null;
  const primaryIsRent = _isRentUnit(primary.unit);
  const primaryRow = _rowRange({
    label: primaryIsRent ? "Canone unitario indicativo" : "Prezzo unitario indicativo",
    low: primary.unit_value_low, central: primary.unit_value_central,
    high: primary.unit_value_high, isRent: primaryIsRent,
    testid: "valuta-range-chart-primary-row",
  });
  const compRow = companion ? _rowRange({
    label: companionMode === "rent" ? "Canone unitario indicativo" : "Prezzo unitario indicativo",
    low: companion.unit_value_low, central: companion.unit_value_central,
    high: companion.unit_value_high,
    isRent: companionMode === "rent",
    testid: "valuta-range-chart-companion-row",
  }) : null;
  return (
    <section className="mb-12" data-testid="valuta-range-chart">
      <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-3">
        Fasce unitarie · €/m²
      </p>
      {primaryRow}
      {compRow}
    </section>
  );
}

function ValutaTerritorialComparison({ result, anchors }) {
  if (!result) return null;
  // §M8.14 · Riferimento territoriale = valore OMI puro della zona,
  // prima degli aggiustamenti per stato/rifiniture del singolo
  // immobile. Cerco quindi l'anchor con ``provider === "omi"``; il
  // vecchio filtro su ``territory_level.includes("omi")`` non
  // matchava perché OMI usa ``territory_level="zone"``, e cadeva
  // sull'anchor market_stats municipality che a volte coincideva
  // per pura fortuna con il valore aggiustato dell'immobile.
  const omiAnchor = (anchors || []).find(
    (a) => a && String(a.provider || "").toLowerCase() === "omi"
             && a.value_central != null,
  );
  const territorial = omiAnchor
    || (anchors || []).find(a => a && ["comune", "municipality", "web_source"].includes((a.territory_level || "").toLowerCase()) && a.value_central != null)
    || null;
  const localCentral = result.unit_value_central;
  const tCentral = territorial?.value_central;
  // §M8.14 · Fallback: se non c'è un valore OMI puro o coincide col
  // valore aggiustato, nascondi la card — non è un confronto
  // significativo.
  if (tCentral == null) return null;
  if (localCentral != null && Math.abs(tCentral - localCentral) < 1) {
    return null;
  }
  return (
    <section className="mb-12" data-testid="valuta-territorial-comparison">
      <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-3">
        Confronto con il territorio
      </p>
      <div className="grid gap-3 md:grid-cols-2">
        <div className="border border-vg2-line rounded p-4"
             data-testid="valuta-territorial-local">
          <p className="text-xs text-vg2-muted uppercase tracking-wider">Il tuo immobile</p>
          <p className="text-2xl font-display font-semibold text-vg2-red mt-1">
            {_isRentUnit(result.unit) ? _fmtEur(localCentral, 2) : _fmtEur(localCentral)}
          </p>
          <p className="text-xs text-vg2-muted mt-1">{_isRentUnit(result.unit) ? "€/m²/mese" : "€/m²"}</p>
        </div>
        <div className="border border-vg2-line rounded p-4"
             data-testid="valuta-territorial-market">
          <p className="text-xs text-vg2-muted uppercase tracking-wider">
            Riferimento territoriale (valore OMI di zona)
          </p>
          <p className="text-2xl font-display font-semibold text-vg2-ink mt-1">
            {_isRentUnit(result.unit) ? _fmtEur(tCentral, 2) : _fmtEur(tCentral)}
          </p>
          <p className="text-xs text-vg2-muted mt-1">
            {(territorial?.territory_name || "Zona OMI") + " · " + (territorial?.period_start || territorial?.period_end || "—")}
          </p>
        </div>
      </div>
    </section>
  );
}

const _OBJECTIVE_CONSEQUENCE_IT = {
  vendere: "Il valore attuale definisce il prezzo di partenza realistico per la trattativa.",
  affittare: "Il canone stimato indica il reddito lordo che il mercato locale riconosce oggi.",
  generare_reddito: "Il canone stimato è la base del rendimento; il valore attuale definisce il capitale impegnato.",
  capire_quanto_vale: "Il valore attuale offre un riferimento numerico per orientare la prossima scelta.",
  capire_quale_scelta_conviene: "Confronta valore e canone per capire quale strada valorizza meglio l'immobile.",
  altro: "Il valore attuale è il tuo punto di partenza per qualsiasi scelta successiva.",
};

function ValutaObjectiveImpact({ objectiveMeta, result, companion, companionMode, hero }) {
  if (!result) return null;
  const code = objectiveMeta?.code || objectiveMeta?.objective_code || "altro";
  const label = objectiveMeta?.label || objectiveMeta?.objective_label || "Obiettivo del proprietario";
  const consequence = _OBJECTIVE_CONSEQUENCE_IT[code] || _OBJECTIVE_CONSEQUENCE_IT.altro;
  const primaryIsRent = _isRentUnit(result.unit);
  const saleRes = primaryIsRent ? companion : result;
  const capitalBase = saleRes?.absolute_value_central ?? null;
  // Correzione 4 · "Valore attuale" coerente col valore corretto per lo stato
  // mostrato in cima alla pagina (stesso campo dinamico, nessun numero fisso).
  const _ca = saleRes?.condition_adjustment;
  const capitalCentral = (_ca && _ca.applied && _ca.current_absolute_value_central != null)
    ? _ca.current_absolute_value_central
    : capitalBase;
  // §BUGFIX-OBJ · l'indicatore di reddito segue l'OPERAZIONE DICHIARATA (hero):
  // rendita lorda mensile dell'attività (casa vacanze / studenti / locazione
  // ordinaria…). Mai il canone ordinario quando è dichiarata un'altra
  // destinazione. Il valore patrimoniale resta distinto.
  const income = hero && hero.mode === "rent" ? hero : null;
  const incomeMonthly = income ? Math.round(Number(income.amount) || 0) : null;
  const incomeInsufficient = hero && hero.mode === "rent-insufficient";
  return (
    <section className="mb-12 border border-vg2-line rounded p-4 bg-vg2-subtle/40"
             data-testid="valuta-objective-impact">
      <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-2">
        Impatto sul tuo obiettivo
      </p>
      <p className="text-sm text-vg2-muted">Obiettivo della tua richiesta</p>
      <p className="text-vg2-ink font-medium mb-3" data-testid="valuta-objective-impact-label">
        {label}
      </p>
      <div className="grid gap-3 md:grid-cols-3 mb-3">
        {capitalCentral != null && (
          <div data-testid="valuta-objective-impact-capital">
            <p className="text-xs text-vg2-muted uppercase">Valore patrimoniale</p>
            <p className="text-lg font-display font-semibold text-vg2-red">
              {_fmtEur(capitalCentral)}
            </p>
          </div>
        )}
        {income != null && (
          <div data-testid="valuta-objective-impact-income">
            <p className="text-xs text-vg2-muted uppercase">
              Rendita lorda mensile · {income.activity}
            </p>
            <p className="text-lg font-display font-semibold text-vg2-ink">
              {_fmtEur(incomeMonthly)}
            </p>
            <p className="text-xs text-vg2-muted mt-1">
              annua {_fmtEur(incomeMonthly * 12)} (× 12)
            </p>
          </div>
        )}
        {income == null && incomeInsufficient && (
          <div data-testid="valuta-objective-impact-income-insufficient">
            <p className="text-xs text-vg2-muted uppercase">
              Rendita{hero.activity ? ` · ${hero.activity}` : ""}
            </p>
            <p className="text-sm text-vg2-ink mt-1">
              Dati non ancora sufficienti per una stima affidabile.
            </p>
          </div>
        )}
      </div>
      <p className="text-sm text-vg2-ink" data-testid="valuta-objective-impact-consequence">
        {consequence}
      </p>
    </section>
  );
}

